import { SimpleOptionType } from '@terragotech/form-renderer';
import {
  AggregateConfig,
  CommandVersionDefinition,
  EventVersionDefinition,
  Gen5Config,
  PropertyCollection,
  WebUIConfig,
  TimerAction,
  LocationProperty,
} from '@terragotech/gen5-config-lib';
import {
  AGGREGATE_DEFAULT_KEYS,
  MAP_SERVICE_DEFAULT_KEYS,
  MAP_SERVICE_TYPE,
  FLOOR_PLAN_DEFAULT_KEYS,
  BUILDING_DEFAULT_KEYS,
  NOT_MAP_ASSETS_NAMES,
  BLUE_FORCE_TRACK_DEFAULT_KEYS,
} from '@terragotech/gen5-shared-utilities';
import { JSONSchema6 } from 'json-schema';
import _ from 'lodash';
import {
  commandActionButtonActionToRef,
  defaultAggregateUICustomization,
  generateAggregateByName,
  generateNewCommandAndVersions,
  generateNewEventAndVersions,
} from './jsonPartsGenerators';
import { getAggregateIndex } from './navigationUtils';
import {
  ActionButtonRow,
  AggregateDefault,
  CommandVersionDefault,
  EventVersionDefault,
  FabAction,
  WebUIDefault,
} from './types';
import { camelCase } from 'change-case';
import { FunctionDefinition } from '@terragotech/gen5-datamapping-lib';

//#region MapService
const MAP_SERVICE_AGGREGATE_DEFAULT: AggregateDefault = {
  typeName: MAP_SERVICE_DEFAULT_KEYS.typeName,
  singularName: 'Map Service',
  properties: {
    symbolKey: {
      uiType: 'Hidden',
    },
    serviceType: {
      nullable: false,
      label: 'Service Type',
      type: 'String',
    },
    serviceUri: {
      nullable: true,
      label: 'Service URL',
      type: 'String',
    },
    layers: {
      nullable: true,
      label: 'Layers',
      type: 'JSON',
      uiType: 'JSON',
    },
  },
  isReferenceType: true,
};
MAP_SERVICE_AGGREGATE_DEFAULT.events = {
  mapServiceCreatedEvent: {
    currentVersion: 1,
    versions: [
      {
        versionNumber: 1,
        eventSchema: {
          properties: {
            data: {
              type: 'object',
              properties: {
                serviceType: { type: 'string' },
                serviceUri: { type: 'string' },
                serviceTitle: { type: 'string' },
                layerGroup: { type: 'string' },
              },
            },
          },
        },
        aggregateMap: {
          nodes: {
            EVENT: { type: 'EVENT', config: {}, inputs: { data: {} } },
            STATE: { type: 'STATE', config: {}, inputs: {} },
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                title: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.serviceTitle',
                },
                createdDateTime: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
                createdBy: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.metadata.userInfo.userName',
                },
                id: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.aggregateId',
                },
                serviceType: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.serviceType',
                },
                serviceUri: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.serviceUri',
                },
                layers: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.layerGroup',
                },
                symbolKey: 'symbol_nodeonly_000000',
              },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
};
MAP_SERVICE_AGGREGATE_DEFAULT.commands = {
  createMapService: {
    defaultLabel: `Create ${
      MAP_SERVICE_AGGREGATE_DEFAULT.singularName ?? MAP_SERVICE_AGGREGATE_DEFAULT.typeName
    }`,
    versions: [
      {
        version: 1,
        commandType: 'FORM',
        isCreationCommand: true,
        hasNoAggregateIdCommand: false,
        fabOptionEnabled: true,
        cmdToEventsMapping: {
          mappingType: 'ONE-TO-MANY',
          ...(true && { eventName: 'mapServiceCreatedEvent' }), // Not applicable for 'ONE-TO-MANY' mappingType; defaults to ''.
          mapping: {
            nodes: {
              OUTPUT: {
                type: 'OUTPUT-EVENT',
                config: {
                  objectSchema: `${MAP_SERVICE_AGGREGATE_DEFAULT.typeName}::mapServiceCreatedEvent::1`,
                  aggregateType: MAP_SERVICE_AGGREGATE_DEFAULT.typeName,
                },
                inputs: {
                  timestamp: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.timestamp',
                  },
                  aggregateId: {
                    sourceObject: 'newNode2',
                    sourcePath: '$.newId',
                  },
                  data: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.data',
                  },
                },
              },
              newNode1: { type: 'COMMAND_DATA', config: {} },
              newNode2: { type: 'ID-GENERATOR', config: {} },
            },
            outputDefinition: {
              outputQuery: { types: ['OUTPUT-EVENT'] },
            },
          },
        },
        formDefinition: {
          order: ['serviceType', 'serviceUri', 'serviceTitle', 'layerGroup'],
          components: {
            serviceType: {
              type: 'select',
              label: 'Service Type',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              options: {
                type: SimpleOptionType,
                items: [
                  { value: MAP_SERVICE_TYPE.WMS, label: 'OpenGIS Web Map Service (WMS)' },
                  { value: MAP_SERVICE_TYPE.ArcGIS, label: 'ArcGIS Map Server' },
                ],
              },
              droppableId: 'form',
            },
            serviceUri: {
              type: 'textInput',
              label: 'Map Service URL',
              placeholder: 'https://www.xyzmapservice.com/api/',
              required: true,
              info: 'A list of the available layers and data are provided by the service endpoint. For WMS, you can view this data via its "GetCapabilities" request. E.g. https://www.xyzmapservice.com/api?request=getcapabilities&service=WMS&version=1.3.0',
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              droppableId: 'form',
            },
            serviceTitle: {
              type: 'textInput',
              label: 'Map Service Title',
              placeholder: 'XYZ Map Service',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              droppableId: 'form',
            },
            layerGroup: {
              template: {
                order: ['layerName', 'layerTitle', 'layerLegendUri'],
                components: {
                  layerName: {
                    type: 'textInput',
                    label: 'Name',
                    placeholder: 'layer_name',
                    required: true,
                    info: 'Comma-separated list of layer Names (e.g. abc,xyz) or IDs (e.g. 10,17,25). For WMS, the layer Name of the Layer XML tag returned from the API. For ArcGIS, the unique ID of the layer. See URL field info for more details.',
                    repeats: {
                      enabled: false,
                      min: 1,
                      max: 1,
                      addButtonText: 'Add Field',
                      deleteConfirmationMessage: 'Are you sure you want to delete this?',
                      allowReordering: true,
                      allowInitialDataDelete: true,
                      allowAdding: true,
                    },
                    droppableId: 'layerGroup',
                  },
                  layerTitle: {
                    type: 'textInput',
                    label: 'Title',
                    placeholder: 'Layer Name',
                    required: false,
                    info: 'A custom title for the layer to display in the app. For WMS, this may be obtained from the Title tag on the Layer returned from the API. For ArcGIS, this will be the layer name alongside its unique ID. See URL field info for more details.',
                    repeats: {
                      enabled: false,
                      min: 1,
                      max: 1,
                      addButtonText: 'Add Field',
                      deleteConfirmationMessage: 'Are you sure you want to delete this?',
                      allowReordering: true,
                      allowInitialDataDelete: true,
                      allowAdding: true,
                    },
                    droppableId: 'layerGroup',
                  },
                  layerLegendUri: {
                    type: 'textInput',
                    label: 'Layer Legend URL',
                    placeholder: 'https://www.xyzmapservice.com/layerLegend.png',
                    required: false,
                    info: "A link to the image to show for the map layer's legend or key. For WMS, this is sometimes provided by the LegendURL tag returned from the API. For ArcGIS, the map server may provide a Legend endpoint instead. See URL field info for more details.",
                    repeats: {
                      enabled: false,
                      min: 1,
                      max: 1,
                      addButtonText: 'Add Field',
                      deleteConfirmationMessage: 'Are you sure you want to delete this?',
                      allowReordering: true,
                      allowInitialDataDelete: true,
                      allowAdding: true,
                    },
                    droppableId: 'layerGroup',
                  },
                },
              },
              type: 'group',
              label: 'Layers',
              repeats: {
                enabled: true,
                min: 1,
                max: -1,
                addButtonText: 'Add Layer',
                deleteConfirmationMessage: 'Are you sure you want to delete this layer?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
            },
          },
        },
      },
    ],
  },
};
const MAP_SERVICE_WEBUI_DEFAULT: WebUIDefault = {
  fabActions: [
    {
      aggregateName: MAP_SERVICE_AGGREGATE_DEFAULT.typeName,
      button: commandActionButtonActionToRef({
        action: `createMapService ver:1`,
        label: `Add ${
          MAP_SERVICE_AGGREGATE_DEFAULT.singularName ?? MAP_SERVICE_AGGREGATE_DEFAULT.typeName
        }`,
        icon: 'fa-object-ungroup',
        conditionalMap: {
          nodes: {
            METADATA: { type: 'METADATA', config: {} },
            BUTTON_STATE: {
              type: 'BUTTON-STATE',
              config: {},
              inputs: {
                isHidden: {
                  sourceObject: 'newNode2',
                  sourcePath: '$.output',
                },
              },
            },
            newNode1: {
              type: 'FIXED-REGEX',
              config: {},
              inputs: {
                text: {
                  sourceObject: 'METADATA',
                  sourcePath: '$.userInfo.permissionString',
                },
                regex: `(:?^|,)(${MAP_SERVICE_DEFAULT_KEYS.permissionKey})(:?$|,)`,
              },
            },
            newNode2: {
              type: 'NOT',
              config: {},
              inputs: {
                input: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.match',
                },
              },
            },
          },
          outputDefinition: { outputNode: 'BUTTON_STATE' },
        },
      } as ActionButtonRow),
    },
  ],
  aggregateUICustomizations: {
    [MAP_SERVICE_AGGREGATE_DEFAULT.typeName]: {
      hiddenFromMapView: true,
    },
  },
};

export const generateMapServiceAggregateData = (config: Gen5Config) => {
  removeAggregateData(config, 'WebMapServiceSource'); // Remove deprecated aggregate definition.
  return generateAggregateData(config, MAP_SERVICE_AGGREGATE_DEFAULT, MAP_SERVICE_WEBUI_DEFAULT);
};
export const removeMapServiceAggregateData = (config: Gen5Config) => {
  removeAggregateData(config, 'WebMapServiceSource'); // Remove deprecated aggregate definition.
  return removeAggregateData(config, MAP_SERVICE_AGGREGATE_DEFAULT.typeName);
};
//#endregion

//#region FloorPlan
const FLOOR_PLAN_AGGREGATE_DEFAULT: AggregateDefault = {
  typeName: FLOOR_PLAN_DEFAULT_KEYS.typeName,
  singularName: 'Floor Plan',
  properties: {
    symbolKey: {
      uiType: 'Hidden',
    },
    [FLOOR_PLAN_DEFAULT_KEYS.parentKey]: {
      nullable: true,
      label: 'Base Floor Plan',
      type: FLOOR_PLAN_DEFAULT_KEYS.typeName,
      relation: 'ONE-TO-ONE',
    },
    subFloorPlans: {
      nullable: true,
      label: 'Sub Floor Plans',
      type: FLOOR_PLAN_DEFAULT_KEYS.typeName,
      relation: 'ONE-TO-MANY',
      foreignColumn: FLOOR_PLAN_DEFAULT_KEYS.parentKey,
    },
    building: {
      nullable: true,
      label: 'Building',
      type: BUILDING_DEFAULT_KEYS.typeName,
      relation: 'ONE-TO-ONE',
    },
    image: {
      nullable: false,
      label: 'Image',
      type: 'PhotoCollection',
    },
  },
  isReferenceType: false,
};
FLOOR_PLAN_AGGREGATE_DEFAULT.events = {
  floorPlanCreated: {
    versions: [
      {
        versionNumber: 1,
        eventSchema: {
          type: 'object',
          properties: {
            type: { type: 'string', enum: ['floorPlanCreated'] },
            version: { type: 'number' },
            timestamp: {
              description: 'Timestamp',
              type: 'string',
              format: 'date-time',
            },
            aggregateId: { type: 'string' },
            metadata: {
              type: 'object',
              properties: {
                timeFormOpened: {
                  type: 'string',
                  format: 'date-time',
                },
                latitude: { type: 'number' },
                longitude: { type: 'number' },
                selectedLatitude: { type: 'number' },
                selectedLongitude: { type: 'number' },
                locationAccuracy: { type: 'number' },
                aggregateType: { type: 'string' },
                userInfo: {
                  type: 'object',
                  properties: {
                    userName: { type: 'string' },
                    email: { type: 'string' },
                    authExpiration: {
                      type: 'string',
                      format: 'date-time',
                    },
                    familyName: { type: 'string' },
                    givenName: { type: 'string' },
                    authPayload: { type: 'object', properties: {} },
                    roles: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          id: { type: 'string' },
                          name: { type: 'string' },
                        },
                      },
                    },
                    roleString: { type: 'string' },
                    permissions: {
                      type: 'array',
                      items: { type: 'string' },
                    },
                    permissionString: { type: 'string' },
                  },
                },
                isOnline: { type: 'boolean' },
                selectedProjects: {
                  type: 'array',
                  items: { type: 'string' },
                },
              },
            },
            data: {
              type: 'object',
              properties: {
                title: { type: 'string' },
                image: {
                  type: 'array',
                  items: { type: 'string' },
                },
                building: { type: 'string' },
                baseFloorPlan: { type: 'string' },
                project: { type: 'string' },
              },
              required: ['title', 'baseFloorPlan', 'building', 'image', 'project'],
            },
          },
        },
        aggregateMap: {
          nodes: {
            EVENT: {
              type: 'EVENT',
              config: {},
              inputs: {},
              position: { x: 134.3125, y: 50 },
            },
            STATE: {
              type: 'STATE',
              config: {},
              inputs: {},
              position: { x: 138.265625, y: 1057.1875 },
            },
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                title: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.title',
                },
                image: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.image',
                },
                building: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.building',
                },
                parentFloorPlan: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.parentFloorPlan',
                },
                baseFloorPlan: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.baseFloorPlan',
                },
                createdBy: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.metadata.userInfo.email',
                },
                createdDateTime: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
                project: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.project',
                },
              },
              position: {
                x: 644.1164021164021,
                y: 425.09810405643736,
              },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
};
FLOOR_PLAN_AGGREGATE_DEFAULT.commands = {
  createFloorPlan: {
    defaultLabel: 'Create Floor Plan',
    versions: [
      {
        version: 1,
        isCreationCommand: true,
        cmdToEventsMapping: {
          mappingType: 'ONE-TO-MANY',
          mapping: {
            nodes: {
              COMMAND: {
                type: 'DATA',
                config: {},
                position: {
                  x: -561.137290996003,
                  y: -44.61228618407615,
                },
              },
              OUTPUT: {
                type: 'OUTPUT-EVENT',
                config: {
                  objectSchema: 'Building::buildingCreated::1',
                  aggregateType: 'Building',
                },
                inputs: {
                  outputEvent: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.output',
                  },
                  aggregateId: {
                    sourceObject: 'newNode2',
                    sourcePath: '$.newId',
                  },
                  data: {
                    title: {
                      sourceObject: 'COMMAND',
                      sourcePath: '$.buildingName',
                    },
                    project: {
                      sourceObject: 'newNode9',
                      sourcePath: '$.output',
                    },
                    lat: {
                      sourceObject: 'newNode11',
                      sourcePath: '$.latitude',
                    },
                    lon: {
                      sourceObject: 'newNode11',
                      sourcePath: '$.longitude',
                    },
                  },
                },
                position: {
                  x: 807.9419635660166,
                  y: -137.98725415121748,
                },
              },
              newNode1: {
                type: 'STRING-EQUALS',
                config: {},
                inputs: {
                  firstSource: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.linkTo',
                  },
                  secondSource: 'newBuilding',
                },
                position: {
                  x: -140.3365288011924,
                  y: -105.87583239746812,
                },
              },
              newNode2: {
                type: 'ID-GENERATOR',
                config: {},
                position: {
                  x: 388.09564835490033,
                  y: -11.918103572022797,
                },
              },
              newNode3: {
                type: 'ARRAY-ITERATOR',
                config: {},
                inputs: {
                  input: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.createFloorPlan',
                  },
                },
                position: {
                  x: 224.62767941032666,
                  y: 159.98442490721052,
                },
              },
              newNode4: {
                type: 'OUTPUT-EVENT',
                config: {
                  name: 'Output Event',
                  aggregateType: 'FloorPlan',
                  objectSchema: 'FloorPlan::floorPlanCreated::1',
                },
                inputs: {
                  aggregateId: {
                    sourceObject: 'newNode3',
                    sourcePath: '$.id',
                  },
                  data: {
                    baseFloorPlan: {
                      sourceObject: 'newNode6',
                      sourcePath: '$.output',
                    },
                    building: {
                      sourceObject: 'newNode5',
                      sourcePath: '$.output',
                    },
                    title: {
                      sourceObject: 'newNode8',
                      sourcePath: '$.title',
                    },
                    image: {
                      sourceObject: 'newNode8',
                      sourcePath: '$.image',
                    },
                    project: {
                      sourceObject: 'newNode9',
                      sourcePath: '$.output',
                    },
                  },
                },
                name: 'Output Event',
                position: {
                  x: 917.4232449482005,
                  y: 198.3130329837099,
                },
              },
              newNode5: {
                type: 'CONDITIONAL-MAPPER',
                config: { type: 'String' },
                inputs: {
                  booleanSource: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.output',
                  },
                  trueSource: {
                    sourceObject: 'newNode2',
                    sourcePath: '$.newId',
                  },
                  falseSource: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.building',
                  },
                },
                position: {
                  x: 594.1236017973164,
                  y: 31.203508338179507,
                },
              },
              newNode6: {
                type: 'CONDITIONAL-MAPPER',
                config: { type: 'String' },
                inputs: {
                  booleanSource: {
                    sourceObject: 'newNode7',
                    sourcePath: '$.output',
                  },
                  trueSource: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.baseFloorPlan',
                  },
                },
                position: {
                  x: 204.08652233870475,
                  y: 611.287955356442,
                },
              },
              newNode7: {
                type: 'STRING-EQUALS',
                config: {},
                inputs: {
                  firstSource: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.linkTo',
                  },
                  secondSource: 'existingFloorPlan',
                },
                position: {
                  x: -41.87230876169522,
                  y: 309.7618094484255,
                },
              },
              newNode8: {
                type: 'OBJECT-ATTRIBUTE-LOOKUP',
                config: {
                  props: [
                    ['String', 'title'],
                    ['Photo Array', 'image'],
                  ],
                },
                inputs: {
                  input: {
                    sourceObject: 'newNode3',
                    sourcePath: '$.output',
                  },
                },
                position: {
                  x: 227.22027290448344,
                  y: 272.30537280701753,
                },
              },
              newNode9: {
                type: 'CONDITIONAL-MAPPER',
                config: { type: 'String' },
                inputs: {
                  booleanSource: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.output',
                  },
                  trueSource: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.project',
                  },
                  falseSource: {
                    sourceObject: 'newNode10',
                    sourcePath: '$.project',
                  },
                },
                position: {
                  x: 37.318939345720935,
                  y: -94.40934659399522,
                },
              },
              newNode10: {
                type: 'SINGLE-AGGREGATE-LOOKUP',
                config: { aggregateType: 'Building' },
                inputs: {
                  aggregateId: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.building',
                  },
                },
                position: {
                  x: -250.61897023182632,
                  y: -106.67019704321052,
                },
              },
              newNode11: {
                type: 'GEOJSON-PARSER',
                config: {},
                inputs: {
                  source: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.location.location',
                  },
                },
                position: { x: 457, y: -192.2265625 },
              },
            },
            outputDefinition: {
              outputQuery: { types: ['OUTPUT-EVENT'] },
            },
          },
        },
        commandType: 'FORM',
        formDefinition: {
          order: [
            'linkTo',
            'project',
            'location',
            'buildingName',
            'building',
            'baseFloorPlan',
            'createFloorPlan',
          ],
          initialDataMap: {
            nodes: {
              STATE: {
                type: 'STATE',
                name: 'Current State',
                inputs: {},
              },
              OUTPUT: {
                type: 'OBJECT-BUILDER',
                name: 'Form State',
                inputs: {},
                config: { objectSchema: 'FORM' },
              },
            },
            outputDefinition: { outputNode: 'OUTPUT' },
          },
          components: {
            linkTo: {
              type: 'radio',
              label: 'Link To',
              placeholder: 'placeholder',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              options: {
                type: 'SimpleOption',
                items: [
                  { value: 'newBuilding', label: 'New Building' },
                  {
                    value: 'existingBuilding',
                    label: 'Existing Building',
                  },
                  {
                    label: 'Existing Floor Plan',
                    value: 'existingFloorPlan',
                  },
                ],
              },
              droppableId: 'form',
            },
            project: {
              type: 'select',
              label: 'Project',
              placeholder: 'placeholder',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              options: {
                type: 'AggregateLoader',
                aggregateType: 'Project',
              },
              conditionalMap: {
                nodes: {
                  FORM: {
                    type: 'FORM',
                    config: {},
                    inputs: {},
                    position: { x: 50, y: 50 },
                  },
                  FORMVALUE: {
                    type: 'FORMVALUE',
                    config: {},
                    inputs: {},
                    position: { x: 64.1875, y: 466.1875 },
                  },
                  FORM_CONDITIONAL: {
                    type: 'FORM-CONDITIONAL',
                    config: {},
                    inputs: {
                      isVisible: {
                        sourceObject: 'newNode1',
                        sourcePath: '$.output',
                      },
                    },
                    position: { x: 799.1875, y: 409.375 },
                  },
                  newNode1: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'newBuilding',
                    },
                    position: { x: 457, y: 308 },
                  },
                },
                outputDefinition: { outputNode: 'FORM_CONDITIONAL' },
              },
              droppableId: 'form',
            },
            location: {
              type: 'location',
              label: 'Location',
              placeholder: 'Enter text',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              conditionalMap: {
                nodes: {
                  FORM: {
                    type: 'FORM',
                    config: {},
                    inputs: {},
                    position: { x: 50, y: 50 },
                  },
                  FORMVALUE: {
                    type: 'FORMVALUE',
                    config: {},
                    inputs: {},
                    position: { x: 64.1875, y: 514.1875 },
                  },
                  FORM_CONDITIONAL: {
                    type: 'FORM-CONDITIONAL',
                    config: {},
                    inputs: {
                      isVisible: {
                        sourceObject: 'newNode1',
                        sourcePath: '$.output',
                      },
                    },
                    position: { x: 985.1875, y: 553.375 },
                  },
                  newNode1: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'newBuilding',
                    },
                    position: { x: 443, y: 417 },
                  },
                },
                outputDefinition: { outputNode: 'FORM_CONDITIONAL' },
              },
              droppableId: 'form',
            },
            buildingName: {
              type: 'textInput',
              label: 'Building Name',
              placeholder: 'XYZ Building',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              conditionalMap: {
                nodes: {
                  FORM: {
                    type: 'FORM',
                    config: {},
                    inputs: {},
                    position: { x: 50, y: 50 },
                  },
                  FORM_CONDITIONAL: {
                    type: 'FORM-CONDITIONAL',
                    config: {},
                    inputs: {
                      isVisible: {
                        sourceObject: 'newNode1',
                        sourcePath: '$.output',
                      },
                    },
                    position: { x: 646.1875, y: 429.375 },
                  },
                  newNode1: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'newBuilding',
                    },
                    position: { x: 331, y: 275 },
                  },
                },
                outputDefinition: { outputNode: 'FORM_CONDITIONAL' },
              },
              errorMap: [
                {
                  name: 'Duplicate Name',
                  description: '',
                  nodes: {
                    FORM: {
                      type: 'FORM',
                      config: {},
                      inputs: {},
                      position: { x: 113.40234375, y: 50 },
                    },
                    FORMVALUE: {
                      type: 'FORMVALUE',
                      config: {},
                      inputs: {},
                      position: { x: 119.58984375, y: 442.59375 },
                    },
                    FIELD_ERROR: {
                      type: 'FORM-ERROR',
                      config: {},
                      inputs: {
                        errorMessage: {
                          sourceObject: 'newNode1',
                          sourcePath: '$.output',
                        },
                      },
                      position: { x: 790, y: 497.1875 },
                    },
                    newNode1: {
                      type: 'CONDITIONAL-MAPPER',
                      config: {
                        type: 'String',
                        name: 'Conditional Mapper',
                      },
                      inputs: {
                        booleanSource: {
                          sourceObject: 'newNode2',
                          sourcePath: '$.exists',
                        },
                        trueSource: 'Building name already exists',
                      },
                      name: 'Conditional Mapper',
                      position: { x: 521, y: 257 },
                    },
                    newNode2: {
                      type: 'AGGREGATE-SEARCH',
                      config: {
                        aggregateType: 'Building',
                        attributeName: 'title',
                      },
                      inputs: {
                        attributeValueSource: {
                          sourceObject: 'FORMVALUE',
                          sourcePath: '$.value',
                        },
                      },
                      position: { x: 178, y: 340 },
                    },
                  },
                  outputDefinition: { outputNode: 'FIELD_ERROR' },
                },
              ],
              droppableId: 'form',
            },
            building: {
              type: 'select',
              label: 'Building',
              placeholder: 'placeholder',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              options: {
                type: 'AggregateLoader',
                aggregateType: 'Building',
              },
              conditionalMap: {
                nodes: {
                  FORM: {
                    type: 'FORM',
                    config: {},
                    inputs: {},
                    position: { x: 50, y: 50 },
                  },
                  FORMVALUE: {
                    type: 'FORMVALUE',
                    config: {},
                    inputs: {},
                    position: { x: 64.1875, y: 418.1875 },
                  },
                  FORM_CONDITIONAL: {
                    type: 'FORM-CONDITIONAL',
                    config: {},
                    inputs: {
                      isVisible: {
                        sourceObject: 'newNode2',
                        sourcePath: '$.output',
                      },
                    },
                    position: { x: 815.1875, y: 356.375 },
                  },
                  newNode1: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'existingBuilding',
                    },
                    position: { x: 348, y: 274 },
                  },
                  newNode2: {
                    type: 'OR',
                    config: {},
                    inputs: {
                      sources: [
                        {
                          sourceObject: 'newNode1',
                          sourcePath: '$.output',
                        },
                        {
                          sourceObject: 'newNode3',
                          sourcePath: '$.output',
                        },
                      ],
                    },
                    position: { x: 577, y: 488 },
                  },
                  newNode3: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'existingFloorPlan',
                    },
                    position: { x: 132, y: 541 },
                  },
                },
                outputDefinition: { outputNode: 'FORM_CONDITIONAL' },
              },
              droppableId: 'form',
            },
            baseFloorPlan: {
              type: 'select',
              label: 'Base Floor Plan',
              placeholder: 'placeholder',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              options: {
                type: 'DynamicLoader',
                optionsMap: {
                  nodes: {
                    FORM: {
                      type: 'FORM',
                      config: {},
                      inputs: {},
                      position: { x: 100.3359375, y: 50 },
                    },
                    FORMVALUE: {
                      type: 'FORMVALUE',
                      config: {},
                      inputs: {},
                      position: { x: 114.5234375, y: 442.1875 },
                    },
                    DROPDOWN_OPTIONS: {
                      type: 'DROPDOWN-OPTIONS',
                      config: {},
                      inputs: {
                        options: {
                          sourceObject: 'newNode2',
                          sourcePath: '$.transformed',
                        },
                      },
                      position: { x: 884, y: 218.375 },
                    },
                    newNode1: {
                      type: 'AGGREGATE-SEARCH',
                      config: {
                        aggregateType: 'FloorPlan',
                        attributeName: 'building',
                      },
                      inputs: {
                        attributeValueSource: {
                          sourceObject: 'FORM',
                          sourcePath: '$.building',
                        },
                      },
                      position: {
                        x: 109.65801668211309,
                        y: 58.58016682113068,
                      },
                    },
                    newNode2: {
                      type: 'ARRAY-TRANSFORM',
                      config: {
                        type: 'Object',
                        functionId: 'transformFloorPlans::1',
                        inputName: 'in',
                        outputName: 'result',
                        inputData: false,
                      },
                      inputs: {
                        data: {
                          sourceObject: 'newNode1',
                          sourcePath: '$.itemsArray',
                        },
                        input: {
                          sourceObject: 'newNode1',
                          sourcePath: '$.itemsArray',
                        },
                      },
                      position: {
                        x: 543.002780352178,
                        y: 322.41334569045415,
                      },
                    },
                  },
                  outputDefinition: { outputNode: 'DROPDOWN_OPTIONS' },
                },
              },
              conditionalMap: {
                nodes: {
                  FORM: {
                    type: 'FORM',
                    config: {},
                    inputs: {},
                    position: { x: 50, y: 50 },
                  },
                  FORMVALUE: {
                    type: 'FORMVALUE',
                    config: {},
                    inputs: {},
                    position: { x: 64.1875, y: 442.1875 },
                  },
                  FORM_CONDITIONAL: {
                    type: 'FORM-CONDITIONAL',
                    config: {},
                    inputs: {
                      isVisible: {
                        sourceObject: 'newNode2',
                        sourcePath: '$.output',
                      },
                    },
                    position: { x: 1080.1875, y: 372.375 },
                  },
                  newNode1: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'existingFloorPlan',
                    },
                    position: { x: 404, y: 373 },
                  },
                  newNode2: {
                    type: 'AND',
                    config: {},
                    inputs: {
                      sources: [
                        {
                          sourceObject: 'newNode1',
                          sourcePath: '$.output',
                        },
                        {
                          sourceObject: 'newNode3',
                          sourcePath: '$.exists',
                        },
                      ],
                    },
                    position: { x: 811, y: 140 },
                  },
                  newNode3: {
                    type: 'TEXT-EXISTS',
                    config: {},
                    inputs: {
                      source: {
                        sourceObject: 'FORM',
                        sourcePath: '$.building',
                      },
                    },
                    position: { x: 331, y: 123 },
                  },
                },
                outputDefinition: { outputNode: 'FORM_CONDITIONAL' },
              },
              droppableId: 'form',
            },
            createFloorPlan: {
              template: {
                order: ['title', 'image'],
                components: {
                  title: {
                    type: 'textInput',
                    label: 'Name',
                    placeholder: 'XYZ Floor Plan',
                    required: true,
                    repeats: {
                      enabled: false,
                      min: 1,
                      max: 1,
                      addButtonText: 'Add Field',
                      deleteConfirmationMessage: 'Are you sure you want to delete this?',
                      allowReordering: true,
                      allowInitialDataDelete: true,
                      allowAdding: true,
                    },
                    errorMap: [
                      {
                        name: 'Duplicate Names within Building',
                        description: '',
                        nodes: {
                          FORM: {
                            type: 'FORM',
                            config: {},
                            inputs: {},
                            position: {
                              x: -231.69454741379917,
                              y: 198.0639993330821,
                            },
                          },
                          FORMVALUE: {
                            type: 'FORMVALUE',
                            config: {},
                            inputs: {},
                            position: {
                              x: -538.4512673930594,
                              y: 638.2756615878955,
                            },
                          },
                          FIELD_ERROR: {
                            type: 'FORM-ERROR',
                            config: {},
                            inputs: {
                              errorMessage: {
                                sourceObject: 'newNode7',
                                sourcePath: '$.output',
                              },
                            },
                            position: {
                              x: 1996.7416954922771,
                              y: 289.82693713172364,
                            },
                          },
                          newNode1: {
                            type: 'ARRAY-FILTER',
                            config: {
                              type: 'Object',
                              functionId: 'filterDuplicateFloorPlanNames::1',
                              inputName: 'in',
                              outputName: 'result',
                              inputData: true,
                            },
                            inputs: {
                              input: {
                                sourceObject: 'newNode4',
                                sourcePath: '$.itemsArray',
                              },
                              data: {
                                floorPlanName: {
                                  sourceObject: 'FORMVALUE',
                                  sourcePath: '$.createFloorPlan.title',
                                },
                              },
                            },
                            position: {
                              x: 559.8788147111723,
                              y: -43.983047105018784,
                            },
                          },
                          newNode4: {
                            type: 'AGGREGATE-SEARCH',
                            config: {
                              aggregateType: 'FloorPlan',
                              attributeName: 'building',
                            },
                            inputs: {
                              attributeValueSource: {
                                sourceObject: 'FORM',
                                sourcePath: '$.building',
                              },
                            },
                            position: {
                              x: 126.14560439715184,
                              y: -4.5642637908086385,
                            },
                          },
                          newNode5: {
                            type: 'ARRAY-LENGTH',
                            config: {},
                            inputs: {
                              input: {
                                sourceObject: 'newNode1',
                                sourcePath: '$.filtered',
                              },
                            },
                            position: {
                              x: 1092.625848362925,
                              y: 262.31047506070695,
                            },
                          },
                          newNode6: {
                            type: 'NUMBER-COMPARE',
                            config: {},
                            inputs: {
                              baseNumber: {
                                sourceObject: 'newNode5',
                                sourcePath: '$.length',
                              },
                              compareNumber: 0,
                            },
                            position: {
                              x: 1293.7686645476251,
                              y: 401.99793165330857,
                            },
                          },
                          newNode7: {
                            type: 'CONDITIONAL-MAPPER',
                            config: { type: 'String' },
                            inputs: {
                              booleanSource: {
                                sourceObject: 'newNode2',
                                sourcePath: '$.output',
                              },
                              trueSource: 'Names of floor plans must be unique within a building',
                            },
                            position: {
                              x: 1723.3453384185823,
                              y: 201.22350592650042,
                            },
                          },
                          newNode9: {
                            type: 'ARRAY-FILTER',
                            config: {
                              type: 'Object',
                              functionId: 'filterDuplicateFloorPlanNames::1',
                              inputName: 'in',
                              outputName: 'result',
                              inputData: true,
                            },
                            inputs: {
                              input: {
                                sourceObject: 'FORM',
                                sourcePath: '$.createFloorPlan',
                              },
                              data: {
                                floorPlanName: {
                                  sourceObject: 'FORMVALUE',
                                  sourcePath: '$.createFloorPlan.title',
                                },
                              },
                            },
                            position: {
                              x: 17.884160756501185,
                              y: 742.7888100866824,
                            },
                          },
                          newNode2: {
                            type: 'OR',
                            config: {},
                            inputs: {
                              sources: [
                                {
                                  sourceObject: 'newNode6',
                                  sourcePath: '$.isGreater',
                                },
                                {
                                  sourceObject: 'newNode8',
                                  sourcePath: '$.isGreater',
                                },
                              ],
                            },
                            position: {
                              x: 1605.9976359338061,
                              y: 627.137903861308,
                            },
                          },
                          newNode3: {
                            type: 'ARRAY-LENGTH',
                            config: {},
                            inputs: {
                              input: {
                                sourceObject: 'newNode9',
                                sourcePath: '$.filtered',
                              },
                            },
                            position: {
                              x: 504.3333333333333,
                              y: 660.521670606777,
                            },
                          },
                          newNode8: {
                            type: 'NUMBER-COMPARE',
                            config: {},
                            inputs: {
                              baseNumber: {
                                sourceObject: 'newNode3',
                                sourcePath: '$.length',
                              },
                              compareNumber: 1,
                            },
                            position: {
                              x: 752.3270291568164,
                              y: 714.1741528762806,
                            },
                          },
                        },
                        outputDefinition: {
                          outputNode: 'FIELD_ERROR',
                        },
                      },
                    ],
                    droppableId: 'createFloorPlan',
                  },
                  image: {
                    type: 'imageupload',
                    label: 'Image',
                    min: 1,
                    max: -27,
                    required: true,
                    droppableId: 'createFloorPlan',
                  },
                },
              },
              type: 'group',
              label: 'Create Floor Plan',
              repeats: {
                enabled: true,
                min: 1,
                max: -1,
                addButtonText: 'Add Floor Plan',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
            },
          },
        },
        hasNoAggregateIdCommand: false,
        fabOptionEnabled: true,
        isImportCommand: false,
        isPublic: false,
      },
    ],
  },
};
const FLOOR_PLAN_WEBUI_DEFAULT: WebUIDefault = {
  fabActions: [
    {
      aggregateName: FLOOR_PLAN_AGGREGATE_DEFAULT.typeName,
      button: commandActionButtonActionToRef({
        action: `createFloorPlan ver:1`,
        label: `Add ${
          FLOOR_PLAN_AGGREGATE_DEFAULT.singularName ?? FLOOR_PLAN_AGGREGATE_DEFAULT.typeName
        }`,
        icon: 'fa-object-ungroup',
        conditionalMap: {
          nodes: {
            METADATA: { type: 'METADATA', config: {} },
            BUTTON_STATE: {
              type: 'BUTTON-STATE',
              config: {},
              inputs: {
                isHidden: {
                  sourceObject: 'newNode2',
                  sourcePath: '$.output',
                },
              },
            },
            newNode1: {
              type: 'FIXED-REGEX',
              config: {},
              inputs: {
                text: {
                  sourceObject: 'METADATA',
                  sourcePath: '$.userInfo.permissionString',
                },
                regex: `(:?^|,)(${FLOOR_PLAN_DEFAULT_KEYS.permissionKey})(:?$|,)`,
              },
            },
            newNode2: {
              type: 'NOT',
              config: {},
              inputs: {
                input: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.match',
                },
              },
            },
          },
          outputDefinition: { outputNode: 'BUTTON_STATE' },
        },
      } as ActionButtonRow),
    },
  ],
  aggregateUICustomizations: {
    [FLOOR_PLAN_AGGREGATE_DEFAULT.typeName]: {
      hiddenFromMapView: true,
    },
  },
};

const FLOOR_PLAN_FUNCTIONS = {
  transformFloorPlans: {
    currentVersion: 1,
    versions: [
      {
        versionNumber: 1,
        output: {
          type: 'object',
          properties: {
            result: {
              type: 'object',
              properties: {
                label: { type: 'string' },
                value: { type: 'string' },
              },
              required: ['label', 'value'],
            },
          },
        },
        input: {
          type: 'object',
          properties: { in: { type: 'object', properties: {} } },
        },
        aggregateMap: {
          nodes: {
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE', name: 'Object Builder' },
              inputs: {
                result: {
                  label: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.title',
                  },
                  value: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.id',
                  },
                },
              },
              name: 'Object Builder',
              position: { x: 1135.5779569892472, y: 192.8481182795699 },
            },
            DATA: {
              type: 'DATA',
              config: {},
              position: { x: 50, y: 289.1875 },
            },
            newNode1: {
              type: 'OBJECT-ATTRIBUTE-LOOKUP',
              config: {
                props: [
                  ['String', 'title'],
                  ['String', 'id'],
                ],
              },
              inputs: {
                input: { sourceObject: 'DATA', sourcePath: '$.in' },
              },
              position: { x: 212.1209677419355, y: 112.77419354838712 },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
  filterDuplicateFloorPlanNames: {
    currentVersion: 1,
    versions: [
      {
        versionNumber: 1,
        output: {
          type: 'object',
          properties: { result: { type: 'boolean' } },
          required: ['result'],
        },
        input: {
          type: 'object',
          properties: {
            in: { type: 'object', properties: {} },
            data: {
              type: 'object',
              properties: { floorPlanName: { type: 'string' } },
              required: ['floorPlanName'],
            },
          },
          required: ['data', 'in'],
        },
        aggregateMap: {
          nodes: {
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                result: {
                  sourceObject: 'newNode2',
                  sourcePath: '$.output',
                },
              },
              position: { x: 744, y: 52 },
            },
            DATA: {
              type: 'DATA',
              config: {},
              position: { x: -6, y: 235 },
            },
            newNode1: {
              type: 'OBJECT-ATTRIBUTE-LOOKUP',
              config: { props: [['String', 'title']] },
              inputs: {
                input: { sourceObject: 'DATA', sourcePath: '$.in' },
              },
              position: { x: 5, y: -28 },
            },
            newNode2: {
              type: 'STRING-EQUALS',
              config: {},
              inputs: {
                firstSource: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.title',
                },
                secondSource: {
                  sourceObject: 'DATA',
                  sourcePath: '$.data.floorPlanName',
                },
              },
              position: { x: 491, y: 161 },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
};

const BLUE_FORCE_TRACK_FUNCTIONS = {
  findIndexGreaterThanValue: {
    currentVersion: 1,
    versions: [
      {
        versionNumber: 1,
        output: {
          type: 'object',
          properties: { result: { type: 'number' } },
        },
        input: {
          type: 'object',
          properties: {
            index: { type: 'number' },
            value: { type: 'number' },
            fullArray: { type: 'array', items: { type: 'number' } },
            data: { type: 'number', title: 'Value' },
            prev: { type: 'number' },
          },
          required: ['value', 'fullArray', 'data', 'prev'],
        },
        aggregateMap: {
          nodes: {
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                result: {
                  sourceObject: 'newNode4',
                  sourcePath: '$.output',
                },
              },
              position: { x: 1947.7894787699104, y: 241.31578945204294 },
            },
            DATA: {
              type: 'DATA',
              config: {},
              position: {
                x: -171.09737276029745,
                y: 141.43158154465945,
              },
            },
            newNode1: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Number' },
              inputs: {
                trueSource: {
                  sourceObject: 'DATA',
                  sourcePath: '$.prev',
                },
                booleanSource: {
                  sourceObject: 'newNode3',
                  sourcePath: '$.exists',
                },
                falseSource: {
                  sourceObject: 'newNode2',
                  sourcePath: '$.length',
                },
              },
              position: { x: 681.8095131647633, y: 422.3716337776801 },
            },
            newNode2: {
              type: 'ARRAY-LENGTH',
              config: { type: 'Number' },
              inputs: {
                input: {
                  sourceObject: 'DATA',
                  sourcePath: '$.fullArray',
                },
              },
              position: { x: 282.56578862967444, y: 284.86842068472987 },
            },
            newNode3: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: { sourceObject: 'DATA', sourcePath: '$.prev' },
              },
              position: { x: 256.25, y: 601.5 },
            },
            newNode4: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Number' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode5',
                  sourcePath: '$.isLess',
                },
                trueSource: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.output',
                },
                falseSource: {
                  sourceObject: 'newNode6',
                  sourcePath: '$.output',
                },
              },
              position: { x: 1620.4030865801274, y: 129.71273992296062 },
            },
            newNode5: {
              type: 'NUMBER-COMPARE',
              config: {},
              inputs: {
                baseNumber: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.output',
                },
                compareNumber: {
                  sourceObject: 'DATA',
                  sourcePath: '$.index',
                },
              },
              position: { x: 1335.50353893531, y: 56.000382276104446 },
            },
            newNode6: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Number' },
              inputs: {
                falseSource: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.output',
                },
                booleanSource: {
                  sourceObject: 'newNode7',
                  sourcePath: '$.isGreater',
                },
                trueSource: {
                  sourceObject: 'DATA',
                  sourcePath: '$.index',
                },
              },
              position: { x: 1020.2697278600111, y: 185.4334246954287 },
            },
            newNode7: {
              type: 'NUMBER-COMPARE',
              config: {},
              inputs: {
                compareNumber: {
                  sourceObject: 'DATA',
                  sourcePath: '$.data',
                },
                baseNumber: {
                  sourceObject: 'DATA',
                  sourcePath: '$.value',
                },
              },
              position: { x: 155.1964243390186, y: -52.35284295915293 },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
  insertItemAtIndex: {
    currentVersion: 1,
    versions: [
      {
        versionNumber: 1,
        output: {
          type: 'object',
          properties: {
            result: { type: 'array', items: { type: 'number' } },
          },
        },
        input: {
          type: 'object',
          properties: {
            in: { type: 'number' },
            index: { type: 'number' },
            data: {
              type: 'object',
              properties: {
                indexToInsert: { type: 'number' },
                valueToInsert: { type: 'number' },
              },
              required: ['indexToInsert', 'valueToInsert'],
            },
            fullArray: { type: 'array', items: { type: 'number' } },
            prev: { type: 'array', items: { type: 'number' } },
          },
          required: ['index', 'data', 'fullArray', 'prev'],
        },
        aggregateMap: {
          nodes: {
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                result: {
                  sourceObject: 'newNode13',
                  sourcePath: '$.output',
                },
              },
              position: { x: 3242.9347947206734, y: 550.4611218643386 },
            },
            DATA: {
              type: 'DATA',
              config: {},
              position: { x: -181.28532262376322, y: 161.2830230721057 },
            },
            newNode1: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Number Array' },
              inputs: {
                falseSource: {
                  sourceObject: 'newNode2',
                  sourcePath: '$.output',
                },
                trueSource: {
                  sourceObject: 'DATA',
                  sourcePath: '$.prev',
                },
                booleanSource: {
                  sourceObject: 'newNode3',
                  sourcePath: '$.exists',
                },
              },
              position: { x: 591.25, y: 351.5 },
            },
            newNode2: {
              type: 'FROM-JSON',
              config: { type: 'Number Array' },
              inputs: { from: '[]' },
              position: { x: 64.4291875347804, y: 629.5522334777137 },
            },
            newNode3: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'DATA',
                  sourcePath: '$.fullArray',
                },
              },
              position: { x: 176.07529079922062, y: 491.286118246401 },
            },
            newNode4: {
              type: 'NUMBER-COMPARE',
              config: { name: 'Number Compare' },
              inputs: {
                baseNumber: {
                  sourceObject: 'DATA',
                  sourcePath: '$.index',
                },
                compareNumber: {
                  sourceObject: 'DATA',
                  sourcePath: '$.data.indexToInsert',
                },
              },
              name: 'Number Compare',
              position: { x: 463.4480637847989, y: -208.6446685336062 },
            },
            newNode5: {
              type: 'ARRAY-LENGTH',
              config: {},
              inputs: {
                input: {
                  sourceObject: 'DATA',
                  sourcePath: '$.fullArray',
                },
              },
              position: { x: 317.874607283386, y: 157.32675177915235 },
            },
            newNode6: {
              type: 'NUMBER-COMPARE',
              config: {},
              inputs: {
                compareNumber: {
                  sourceObject: 'newNode5',
                  sourcePath: '$.length',
                },
                baseNumber: {
                  sourceObject: 'DATA',
                  sourcePath: '$.data.indexToInsert',
                },
              },
              position: { x: 629.385699527751, y: 82.46711501814104 },
            },
            newNode7: {
              type: 'ARRAY-CONCAT',
              config: { type: 'Number' },
              inputs: {
                array2: {
                  sourceObject: 'newNode8',
                  sourcePath: '$.output',
                },
                array1: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 1605.9175912251771,
                y: -21.913176669205157,
              },
            },
            newNode8: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Number Array' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode4',
                  sourcePath: '$.isEqual',
                },
                trueSource: [
                  {
                    sourceObject: 'DATA',
                    sourcePath: '$.data.valueToInsert',
                  },
                ],
              },
              position: {
                x: 1078.0029669769167,
                y: -249.22438574540337,
              },
            },
            newNode9: {
              type: 'ARRAY-CONCAT',
              config: { type: 'Number' },
              inputs: {
                array1: {
                  sourceObject: 'newNode7',
                  sourcePath: '$.output',
                },
                array2: [{ sourceObject: 'DATA', sourcePath: '$.in' }],
              },
              position: { x: 2059.0032900353985, y: 283.9318267338542 },
            },
            newNode10: {
              type: 'SUBTRACTION',
              config: {},
              inputs: {
                number1: {
                  sourceObject: 'newNode5',
                  sourcePath: '$.length',
                },
                number2: 1,
              },
              position: { x: 1076.3308926381796, y: 188.87015499464064 },
            },
            newNode11: {
              type: 'NUMBER-COMPARE',
              config: {},
              inputs: {
                baseNumber: {
                  sourceObject: 'DATA',
                  sourcePath: '$.index',
                },
                compareNumber: {
                  sourceObject: 'newNode10',
                  sourcePath: '$.result',
                },
              },
              position: { x: 1500.7923106365283, y: 329.25192605138625 },
            },
            newNode12: {
              type: 'AND',
              config: {},
              inputs: {
                sources: [
                  { sourceObject: 'newNode11', sourcePath: '$.isEqual' },
                  { sourceObject: 'newNode6', sourcePath: '$.isEqual' },
                ],
              },
              position: { x: 1859.7652574680972, y: 631.8152306642855 },
            },
            newNode13: {
              type: 'ARRAY-CONCAT',
              config: { type: 'Number' },
              inputs: {
                array1: {
                  sourceObject: 'newNode9',
                  sourcePath: '$.output',
                },
                array2: {
                  sourceObject: 'newNode14',
                  sourcePath: '$.output',
                },
              },
              position: { x: 2672.01285813285, y: 713.5688614449411 },
            },
            newNode14: {
              type: 'CONDITIONAL-MAPPER',
              config: {
                type: 'Number Array',
                name: 'Conditional Mapper',
              },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode12',
                  sourcePath: '$.output',
                },
                trueSource: [
                  {
                    sourceObject: 'DATA',
                    sourcePath: '$.data.valueToInsert',
                  },
                ],
              },
              name: 'Conditional Mapper',
              position: { x: 2205.205922859315, y: 816.0724614753316 },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
  insertItemsAtIndex: {
    currentVersion: 1,
    versions: [
      {
        versionNumber: 1,
        output: {
          type: 'object',
          properties: {
            result: { type: 'array', items: { type: 'number' } },
          },
        },
        input: {
          type: 'object',
          properties: {
            in: { type: 'number' },
            index: { type: 'number' },
            data: {
              type: 'object',
              properties: {
                indexToInsert: { type: 'number' },
                valueToInsert: {
                  type: 'array',
                  items: { type: 'number' },
                },
              },
              required: ['indexToInsert', 'valueToInsert'],
            },
            fullArray: { type: 'array', items: { type: 'number' } },
            prev: { type: 'array', items: { type: 'number' } },
          },
          required: ['index', 'data', 'fullArray', 'prev'],
        },
        aggregateMap: {
          nodes: {
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                result: {
                  sourceObject: 'newNode13',
                  sourcePath: '$.output',
                },
              },
              position: { x: 3242.9347947206734, y: 550.4611218643386 },
            },
            DATA: {
              type: 'DATA',
              config: {},
              position: { x: -181.28532262376322, y: 161.2830230721057 },
            },
            newNode1: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Number Array' },
              inputs: {
                falseSource: {
                  sourceObject: 'newNode2',
                  sourcePath: '$.output',
                },
                trueSource: {
                  sourceObject: 'DATA',
                  sourcePath: '$.prev',
                },
                booleanSource: {
                  sourceObject: 'newNode3',
                  sourcePath: '$.exists',
                },
              },
              position: { x: 591.25, y: 351.5 },
            },
            newNode2: {
              type: 'FROM-JSON',
              config: { type: 'Number Array' },
              inputs: { from: '[]' },
              position: { x: 64.4291875347804, y: 629.5522334777137 },
            },
            newNode3: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'DATA',
                  sourcePath: '$.fullArray',
                },
              },
              position: { x: 176.07529079922062, y: 491.286118246401 },
            },
            newNode4: {
              type: 'NUMBER-COMPARE',
              config: { name: 'Number Compare' },
              inputs: {
                baseNumber: {
                  sourceObject: 'DATA',
                  sourcePath: '$.index',
                },
                compareNumber: {
                  sourceObject: 'DATA',
                  sourcePath: '$.data.indexToInsert',
                },
              },
              name: 'Number Compare',
              position: { x: 463.4480637847989, y: -208.6446685336062 },
            },
            newNode5: {
              type: 'ARRAY-LENGTH',
              config: {},
              inputs: {
                input: {
                  sourceObject: 'DATA',
                  sourcePath: '$.fullArray',
                },
              },
              position: { x: 317.874607283386, y: 157.32675177915235 },
            },
            newNode6: {
              type: 'NUMBER-COMPARE',
              config: {},
              inputs: {
                compareNumber: {
                  sourceObject: 'newNode5',
                  sourcePath: '$.length',
                },
                baseNumber: {
                  sourceObject: 'DATA',
                  sourcePath: '$.data.indexToInsert',
                },
              },
              position: { x: 629.385699527751, y: 82.46711501814104 },
            },
            newNode7: {
              type: 'ARRAY-CONCAT',
              config: { type: 'Number' },
              inputs: {
                array2: {
                  sourceObject: 'newNode8',
                  sourcePath: '$.output',
                },
                array1: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 1605.9175912251771,
                y: -21.913176669205157,
              },
            },
            newNode8: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Number Array' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode4',
                  sourcePath: '$.isEqual',
                },
                trueSource: {
                  sourceObject: 'DATA',
                  sourcePath: '$.data.valueToInsert',
                },
              },
              position: {
                x: 1001.6945798246537,
                y: -249.22438574540337,
              },
            },
            newNode9: {
              type: 'ARRAY-CONCAT',
              config: { type: 'Number' },
              inputs: {
                array1: {
                  sourceObject: 'newNode7',
                  sourcePath: '$.output',
                },
                array2: [{ sourceObject: 'DATA', sourcePath: '$.in' }],
              },
              position: { x: 2059.0032900353985, y: 283.9318267338542 },
            },
            newNode10: {
              type: 'SUBTRACTION',
              config: {},
              inputs: {
                number1: {
                  sourceObject: 'newNode5',
                  sourcePath: '$.length',
                },
                number2: 1,
              },
              position: { x: 1076.3308926381796, y: 188.87015499464064 },
            },
            newNode11: {
              type: 'NUMBER-COMPARE',
              config: {},
              inputs: {
                baseNumber: {
                  sourceObject: 'DATA',
                  sourcePath: '$.index',
                },
                compareNumber: {
                  sourceObject: 'newNode10',
                  sourcePath: '$.result',
                },
              },
              position: { x: 1500.7923106365283, y: 329.25192605138625 },
            },
            newNode12: {
              type: 'AND',
              config: {},
              inputs: {
                sources: [
                  { sourceObject: 'newNode11', sourcePath: '$.isEqual' },
                  { sourceObject: 'newNode6', sourcePath: '$.isEqual' },
                ],
              },
              position: { x: 1859.7652574680972, y: 631.8152306642855 },
            },
            newNode13: {
              type: 'ARRAY-CONCAT',
              config: { type: 'Number' },
              inputs: {
                array1: {
                  sourceObject: 'newNode9',
                  sourcePath: '$.output',
                },
                array2: {
                  sourceObject: 'newNode14',
                  sourcePath: '$.output',
                },
              },
              position: { x: 2672.01285813285, y: 713.5688614449411 },
            },
            newNode14: {
              type: 'CONDITIONAL-MAPPER',
              config: {
                type: 'Number Array',
                name: 'Conditional Mapper',
              },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode12',
                  sourcePath: '$.output',
                },
                trueSource: {
                  sourceObject: 'DATA',
                  sourcePath: '$.data.valueToInsert',
                },
              },
              name: 'Conditional Mapper',
              position: { x: 2186.3565926990955, y: 750.099805914564 },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
  transformBFTArray: {
    currentVersion: 1,
    versions: [
      {
        versionNumber: 1,
        output: {
          type: 'object',
          properties: {
            result: {
              type: 'object',
              properties: {
                coordinates: {
                  type: 'array',
                  items: { type: 'array', items: { type: 'number' } },
                },
                times: { type: 'array', items: { type: 'number' } },
              },
              required: ['coordinates', 'times'],
            },
          },
        },
        input: {
          type: 'object',
          properties: {
            in: {
              type: 'object',
              properties: {
                Lat: { type: 'number' },
                Lon: { type: 'number' },
                Timestamp: { type: 'number' },
              },
              required: ['Lat', 'Timestamp', 'Lon'],
            },
            prev: {
              type: 'object',
              properties: {
                coordinates: {
                  type: 'array',
                  items: { type: 'array', items: { type: 'number' } },
                },
                times: { type: 'array', items: { type: 'number' } },
              },
              required: ['coordinates', 'times'],
            },
          },
          required: ['prev'],
        },
        aggregateMap: {
          nodes: {
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                result: {
                  times: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.output',
                  },
                  coordinates: {
                    sourceObject: 'newNode6',
                    sourcePath: '$.output',
                  },
                },
              },
              position: { x: 1926.6586399661737, y: 261.47871270533017 },
            },
            DATA: {
              type: 'DATA',
              config: {},
              position: {
                x: -1143.6842289142992,
                y: 236.85128428275075,
              },
            },
            newNode1: {
              type: 'ARRAY-CONCAT',
              config: { type: 'Number' },
              inputs: {
                array1: {
                  sourceObject: 'newNode7',
                  sourcePath: '$.output',
                },
                array2: [{ sourceObject: 'newNode14', sourcePath: '$.value' }],
              },
              position: { x: 1369.0905776806771, y: 769.0633880493396 },
            },
            newNode2: {
              type: 'NUMBER-TO-TEXT',
              config: {},
              inputs: {
                input: { sourceObject: 'DATA', sourcePath: '$.in.Lat' },
              },
              position: {
                x: -1.8669854137293953,
                y: 30.348745147774714,
              },
            },
            newNode3: {
              type: 'NUMBER-TO-TEXT',
              config: {},
              inputs: {
                input: { sourceObject: 'DATA', sourcePath: '$.in.Lon' },
              },
              position: { x: 18.771925703654233, y: 210.3384753145446 },
            },
            newNode4: {
              type: 'STRING-CONCAT',
              config: {},
              inputs: {
                strings: [
                  '[[',
                  { sourceObject: 'newNode3', sourcePath: '$.outText' },
                  ',',
                  { sourceObject: 'newNode2', sourcePath: '$.outText' },
                  ']]',
                ],
              },
              position: { x: 660.4950082145933, y: 26.10735132082837 },
            },
            newNode5: {
              type: 'FROM-JSON',
              config: { type: 'Number Array', name: 'From JSON' },
              inputs: {
                from: {
                  sourceObject: 'newNode4',
                  sourcePath: '$.output',
                },
              },
              name: 'From JSON',
              position: { x: 1131.162121858576, y: 83.76742507208547 },
            },
            newNode6: {
              type: 'ARRAY-CONCAT',
              config: { type: 'Number' },
              inputs: {
                array1: {
                  sourceObject: 'newNode8',
                  sourcePath: '$.output',
                },
                array2: {
                  sourceObject: 'newNode5',
                  sourcePath: '$.output',
                },
              },
              position: { x: 1395.8245039568276, y: 216.83211100144626 },
            },
            newNode7: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Number Array' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode9',
                  sourcePath: '$.exists',
                },
                trueSource: {
                  sourceObject: 'DATA',
                  sourcePath: '$.prev.times',
                },
                falseSource: {
                  sourceObject: 'newNode11',
                  sourcePath: '$.output',
                },
              },
              position: { x: 535.0753620142817, y: 859.2638802627796 },
            },
            newNode8: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Number Array' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode10',
                  sourcePath: '$.exists',
                },
                trueSource: {
                  sourceObject: 'DATA',
                  sourcePath: '$.prev.coordinates',
                },
                falseSource: {
                  sourceObject: 'newNode11',
                  sourcePath: '$.output',
                },
              },
              position: { x: 508.1682421242724, y: 432.7421252989152 },
            },
            newNode9: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'DATA',
                  sourcePath: '$.prev.times',
                },
              },
              position: { x: 8.61431921173164, y: 803.0378634670594 },
            },
            newNode10: {
              type: 'TEXT-EXISTS',
              config: { name: 'Exists' },
              inputs: {
                source: {
                  sourceObject: 'DATA',
                  sourcePath: '$.prev.coordinates',
                },
              },
              name: 'Exists',
              position: { x: 34.848373953163524, y: 396.8427615192943 },
            },
            newNode12: {
              type: 'STRING-CONCAT',
              config: {},
              inputs: { strings: ['[]'] },
              position: { x: -475.2743514904015, y: 1161.8767110258234 },
            },
            newNode11: {
              type: 'FROM-JSON',
              config: { type: 'Number Array' },
              inputs: {
                from: {
                  sourceObject: 'newNode12',
                  sourcePath: '$.output',
                },
              },
              position: { x: 24.89326500306143, y: 1180.6497582936745 },
            },
            newNode13: {
              type: 'NUMBER-TO-TEXT',
              config: {},
              inputs: {
                input: {
                  sourceObject: 'DATA',
                  sourcePath: '$.in.Timestamp',
                },
              },
              position: { x: -32.00880337273505, y: 578.145930814526 },
            },
            newNode14: {
              type: 'TEXT-TO-NUMBER',
              config: {},
              inputs: {
                inText: {
                  sourceObject: 'newNode13',
                  sourcePath: '$.outText',
                },
              },
              position: { x: 698.7349011175561, y: 632.6113000933054 },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
};

export const generateFloorPlanAggregateData = (config: Gen5Config) => {
  generateAggregateData(config, FLOOR_PLAN_AGGREGATE_DEFAULT, FLOOR_PLAN_WEBUI_DEFAULT);
  addFloorPlanPropertyToAggregates(config);
  addFloorPlanFunctions(config);
};
export const removeFloorPlanAggregateData = (config: Gen5Config) => {
  removeAggregateData(config, FLOOR_PLAN_DEFAULT_KEYS.typeName);
  removePropertiesFromAggregatesByType(config, FLOOR_PLAN_DEFAULT_KEYS.typeName);
  removeTransformFloorPlanFunction(config);
};
export const removeAggregatePropertyFromFloorPlanAndProject = (
  config: Gen5Config,
  floorPlanEnabled: boolean,
  typeName: string,
  setAggregateProperties: (aggrIndex: number, aggregateProperties: PropertyCollection) => void
): void => {
  const getAggregateByTypeName = (typeName: string) => {
    return Object.entries(config.aggregates).filter(
      ([id, aggregate]) => aggregate.typeName === typeName
    );
  };

  const getFilteredProperties = (aggregateTypeName: string) => {
    const aggregate = getAggregateByTypeName(aggregateTypeName);

    aggregate.forEach(([index, aggregate]) => {
      const filteredProps: PropertyCollection = {
        id: { type: 'ID', label: 'ID' },
        ...Object.fromEntries(
          Object.entries(aggregate.properties).filter(([, prop]) => prop.type !== typeName)
        ),
      };
      setAggregateProperties(Number(index), filteredProps);
    });
  };

  if (floorPlanEnabled) {
    getFilteredProperties(FLOOR_PLAN_DEFAULT_KEYS.typeName);
  }
  getFilteredProperties('Project');
};
const addFloorPlanPropertyToAggregates = (config: Gen5Config) => {
  Object.entries(config.aggregates).forEach(([recordType, aggregate]) => {
    const defaultFloorPlanProperty = {
      nullable: true,
      label: 'Floor Plan',
      type: FLOOR_PLAN_DEFAULT_KEYS.typeName,
      relation: 'ONE-TO-ONE',
    };

    if (
      aggregate.typeName !== FLOOR_PLAN_DEFAULT_KEYS.typeName &&
      aggregate.typeName !== BUILDING_DEFAULT_KEYS.typeName &&
      !NOT_MAP_ASSETS_NAMES.includes(aggregate.typeName)
    ) {
      aggregate.properties = {
        ...aggregate.properties,
        floorPlan: {
          ...defaultFloorPlanProperty,
          ...(aggregate.properties?.floorPlan ?? {}),
        },
      };
    }

    if (aggregate.typeName === 'Project') {
      aggregate.properties = {
        ...aggregate.properties,
        floorPlans: {
          nullable: true,
          label: 'Floor Plans',
          type: FLOOR_PLAN_DEFAULT_KEYS.typeName,
          relation: 'ONE-TO-MANY',
          foreignColumn: 'project',
        },
        buildings: {
          nullable: true,
          label: 'Buildings',
          type: BUILDING_DEFAULT_KEYS.typeName,
          relation: 'ONE-TO-MANY',
          foreignColumn: 'project',
        },
      };
    }
  });

  const floorPlanAggregate = config.aggregates.find(
    (aggregate) => aggregate.typeName === FLOOR_PLAN_DEFAULT_KEYS.typeName
  );

  if (floorPlanAggregate) {
    floorPlanAggregate.properties = {
      ...floorPlanAggregate.properties,
      ...Object.entries(config.aggregates).reduce((acc, [recordType, aggregate]) => {
        if (
          aggregate.typeName === FLOOR_PLAN_DEFAULT_KEYS.typeName ||
          aggregate.typeName === BUILDING_DEFAULT_KEYS.typeName ||
          NOT_MAP_ASSETS_NAMES.includes(aggregate.typeName)
        )
          return acc;
        acc[`${camelCase(aggregate.typeName)}s`] = {
          nullable: true,
          label: aggregate.pluralName,
          type: aggregate.typeName,
          relation: 'ONE-TO-MANY',
          foreignColumn: FLOOR_PLAN_DEFAULT_KEYS.foreignKey,
        };
        return acc;
      }, {} as Record<string, any>),
    };
  }
};

const addBlueForceTrackFunctions = (config: Gen5Config) => {
  if (config?.functions) {
    for (const key of Object.keys(BLUE_FORCE_TRACK_FUNCTIONS)) {
      if (!config.functions[key]) {
        config.functions[key] = (BLUE_FORCE_TRACK_FUNCTIONS as Record<string, FunctionDefinition>)[
          key
        ];
      }
    }
  }
};

const addFloorPlanFunctions = (config: Gen5Config) => {
  if (config?.functions) {
    config.functions.transformFloorPlans =
      FLOOR_PLAN_FUNCTIONS.transformFloorPlans as FunctionDefinition;
    config.functions.filterDuplicateFloorPlanNames =
      FLOOR_PLAN_FUNCTIONS.filterDuplicateFloorPlanNames as FunctionDefinition;
  }
};

const removeTransformFloorPlanFunction = (config: Gen5Config) => {
  if (config?.functions && 'transformFloorPlans' in config.functions) {
    delete config.functions.transformFloorPlans;
  }
  if (config?.functions && 'filterDuplicateFloorPlanNames' in config.functions) {
    delete config.functions.filterDuplicateFloorPlanNames;
  }
};
//#endregion

//#region Building
const BUILDING_AGGREGATE_DEFAULT: AggregateDefault = {
  typeName: BUILDING_DEFAULT_KEYS.typeName,
  singularName: 'Building',
  properties: {
    floorPlans: {
      nullable: true,
      label: 'Floor Plans',
      type: FLOOR_PLAN_DEFAULT_KEYS.typeName,
      relation: 'ONE-TO-MANY',
      foreignColumn: BUILDING_DEFAULT_KEYS.foreignKey,
    },
  },
  isReferenceType: false,
};
BUILDING_AGGREGATE_DEFAULT.events = {
  buildingCreated: {
    versions: [
      {
        versionNumber: 1,
        eventSchema: {
          type: 'object',
          properties: {
            type: { type: 'string', enum: ['buildingCreated'] },
            version: { type: 'number' },
            timestamp: {
              description: 'Timestamp',
              type: 'string',
              format: 'date-time',
            },
            aggregateId: { type: 'string' },
            metadata: {
              type: 'object',
              properties: {
                timeFormOpened: {
                  type: 'string',
                  format: 'date-time',
                },
                latitude: { type: 'number' },
                longitude: { type: 'number' },
                selectedLatitude: { type: 'number' },
                selectedLongitude: { type: 'number' },
                locationAccuracy: { type: 'number' },
                aggregateType: { type: 'string' },
                userInfo: {
                  type: 'object',
                  properties: {
                    userName: { type: 'string' },
                    email: { type: 'string' },
                    authExpiration: {
                      type: 'string',
                      format: 'date-time',
                    },
                    familyName: { type: 'string' },
                    givenName: { type: 'string' },
                    authPayload: { type: 'object', properties: {} },
                    roles: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          id: { type: 'string' },
                          name: { type: 'string' },
                        },
                      },
                    },
                    roleString: { type: 'string' },
                    permissions: {
                      type: 'array',
                      items: { type: 'string' },
                    },
                    permissionString: { type: 'string' },
                  },
                },
                isOnline: { type: 'boolean' },
                selectedProjects: {
                  type: 'array',
                  items: { type: 'string' },
                },
              },
            },
            data: {
              type: 'object',
              properties: {
                title: { type: 'string' },
                project: { type: 'string' },
                lat: { type: 'number' },
                lon: { type: 'number' },
              },
              required: ['title', 'project', 'lat', 'lon'],
            },
          },
        },
        aggregateMap: {
          nodes: {
            EVENT: {
              type: 'EVENT',
              config: {},
              inputs: {},
              position: { x: 134.3125, y: 50 },
            },
            STATE: {
              type: 'STATE',
              config: {},
              inputs: {},
              position: { x: 138.265625, y: 985.1875 },
            },
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                title: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.title',
                },
                createdDateTime: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
                createdBy: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.metadata.userInfo.email',
                },
                lat: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.lat',
                },
                lon: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.lon',
                },
                project: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.project',
                },
                symbolKey: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 587.2182026620326,
                y: 185.99164361356458,
              },
            },
            newNode1: {
              type: 'STRING-CONCAT',
              config: {},
              inputs: { strings: ['symbol_circle_000000__1'] },
              position: { x: 78, y: 360.5 },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
};

export const generateBuildingAggregateData = (config: Gen5Config) => {
  return generateAggregateData(config, BUILDING_AGGREGATE_DEFAULT);
};
export const removeBuildingAggregateData = (config: Gen5Config) => {
  removeAggregateData(config, BUILDING_DEFAULT_KEYS.typeName);
  removePropertiesFromAggregatesByType(config, BUILDING_DEFAULT_KEYS.typeName);
};
//#endregion

//#region BlueForceTrack
const BLUE_FORCE_TRACK_AGGREGATE_DEFAULT: AggregateDefault = {
  typeName: BLUE_FORCE_TRACK_DEFAULT_KEYS.typeName,
  pluralName: 'User Locations',
  singularName: 'User Location',
  objectStore: {
    rebuildOnStartup: true,
    table: 'blueForceTracks',
    type: 'Postgres',
    reducerName: 'EVENT_MAP',
    schema: 'public',
  },
  labelProperty: 'title',
  derivedPropertyMapping: {
    nodes: {
      OUTPUT: {
        type: 'OBJECT-BUILDER',
        name: 'Output State',
        inputs: {},
        config: { objectSchema: 'STATE' },
      },
    },
    outputDefinition: { outputNode: 'OUTPUT' },
  },
  properties: {
    symbolKey: {
      label: 'Symbol Key',
      type: 'String',
      uiType: 'Symbol Key',
      filterOptions: 'DISTINCT',
      filterable: true,
      hideFromAssetAttributes: true,
      nullable: true,
    },
    title: {
      label: 'Title',
      type: 'String',
      uiType: 'Title',
      filterOptions: 'DISTINCT',
      filterable: true,
      nullable: true,
    },
    id: {
      label: 'Id',
      type: 'ID',
      uiType: 'Hidden',
      filterOptions: 'DISTINCT',
      filterable: true,
      hideFromAssetAttributes: true,
      nullable: true,
    },
    createdBy: {
      label: 'User',
      type: 'String',
      filterOptions: 'DISTINCT',
      filterable: true,
      nullable: true,
    },
    createdDateTime: {
      label: 'Created Date/Time',
      type: 'DateTime',
      filterOptions: 'DISTINCT',
      filterable: true,
      nullable: true,
    },
    modifiedDateTime: {
      label: 'Modified Date/Time',
      type: 'DateTime',
      uiType: 'Hidden',
      filterOptions: 'DISTINCT',
      filterable: true,
      nullable: true,
    },
    polyline: {
      label: 'Track',
      type: 'Geography',
      uiType: 'JSON',
      nullable: true,
    },
    assetAttributes: {
      label: 'Asset Attributes',
      type: 'JSON',
      uiType: 'Hidden',
      filterOptions: 'DISTINCT',
      filterable: false,
      nullable: true,
    },
    timestamps: {
      label: 'Timestamps',
      type: 'String',
      uiType: 'Hidden',
      filterable: false,
      nullable: true,
    },
  },
  isReferenceType: false,
  primaryLocationProperty: 'polyline',
};
BLUE_FORCE_TRACK_AGGREGATE_DEFAULT.events = {
  pointAdded: {
    versions: [
      {
        versionNumber: 1,
        eventSchema: {
          type: 'object',
          properties: {
            type: { type: 'string', enum: ['addPoint'] },
            version: { type: 'number' },
            timestamp: {
              description: 'Timestamp',
              type: 'string',
              format: 'date-time',
            },
            aggregateId: { type: 'string' },
            metadata: {
              type: 'object',
              properties: {
                timeFormOpened: {
                  type: 'string',
                  format: 'date-time',
                },
                latitude: { type: 'number' },
                longitude: { type: 'number' },
                selectedLatitude: { type: 'number' },
                selectedLongitude: { type: 'number' },
                locationAccuracy: { type: 'number' },
                aggregateType: { type: 'string' },
                userInfo: {
                  type: 'object',
                  properties: {
                    userName: { type: 'string' },
                    email: { type: 'string' },
                    authExpiration: {
                      type: 'string',
                      format: 'date-time',
                    },
                    familyName: { type: 'string' },
                    givenName: { type: 'string' },
                    authPayload: { type: 'object', properties: {} },
                    roles: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          id: { type: 'string' },
                          name: { type: 'string' },
                        },
                      },
                    },
                    roleString: { type: 'string' },
                    permissions: {
                      type: 'array',
                      items: { type: 'string' },
                    },
                    permissionString: { type: 'string' },
                  },
                },
                isOnline: { type: 'boolean' },
                selectedProjects: {
                  type: 'array',
                  items: { type: 'string' },
                },
              },
            },
            data: {
              type: 'object',
              properties: {
                lat: { type: 'number' },
                lon: { type: 'number' },
                project: { type: 'string' },
              },
              required: ['lat', 'lon', 'project'],
            },
          },
        },
        aggregateMap: {
          nodes: {
            EVENT: {
              type: 'EVENT',
              config: {},
              inputs: {},
              position: {
                x: -1378.5672749951912,
                y: 120.91602739277374,
              },
            },
            STATE: {
              type: 'STATE',
              config: { name: 'Current Aggregate State' },
              inputs: {},
              name: 'Current Aggregate State',
              position: {
                x: -328.73580004190813,
                y: 635.2262610121452,
              },
            },
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                id: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.aggregateId',
                },
                createdBy: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.output',
                },
                modifiedDateTime: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
                createdDateTime: {
                  sourceObject: 'newNode5',
                  sourcePath: '$.output',
                },
                polyline: {
                  sourceObject: 'newNode12',
                  sourcePath: '$.geojson',
                },
                symbolKey: 'line_solid_2_0000FF',
                title: {
                  sourceObject: 'newNode8',
                  sourcePath: '$.output',
                },
                timestamps: {
                  sourceObject: 'newNode33',
                  sourcePath: '$.result',
                },
                project: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.project',
                },
              },
              position: {
                x: 4268.678936821423,
                y: 503.1443173404304,
              },
            },
            newNode1: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'String' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode3',
                  sourcePath: '$.exists',
                },
                trueSource: {
                  sourceObject: 'newNode2',
                  sourcePath: '$.output',
                },
                falseSource: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.metadata.userInfo.userName',
                },
              },
              position: {
                x: 969.0593011767274,
                y: 467.6990821536299,
              },
            },
            newNode2: {
              type: 'STRING-CONCAT',
              config: { name: 'Text Combine' },
              inputs: {
                delimiter: ' ',
                strings: [
                  {
                    sourceObject: 'EVENT',
                    sourcePath: '$.metadata.userInfo.givenName',
                  },
                  {
                    sourceObject: 'EVENT',
                    sourcePath: '$.metadata.userInfo.familyName',
                  },
                ],
              },
              name: 'Text Combine',
              position: {
                x: 542.1270258542785,
                y: 606.7289009185772,
              },
            },
            newNode3: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.metadata.userInfo.givenName',
                },
              },
              position: {
                x: 551.9698448818854,
                y: 390.1868823112256,
              },
            },
            newNode4: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'STATE',
                  sourcePath: '$.createdDateTime',
                },
              },
              position: {
                x: 460.9928857054535,
                y: 948.235062665057,
              },
            },
            newNode5: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'String' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode4',
                  sourcePath: '$.exists',
                },
                trueSource: {
                  sourceObject: 'STATE',
                  sourcePath: '$.createdDateTime',
                },
                falseSource: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
              },
              position: {
                x: 956.4523326661688,
                y: 810.0435073988186,
              },
            },
            newNode6: {
              type: 'DATE-STRING-PARSER',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
              },
              position: {
                x: 474.4576450394811,
                y: -74.88631674320027,
              },
            },
            newNode7: {
              type: 'DATE-INFO',
              config: {},
              inputs: {
                date: {
                  sourceObject: 'newNode6',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 753.804549113468,
                y: -190.1790023674313,
              },
            },
            newNode8: {
              type: 'STRING-CONCAT',
              config: { name: 'Text Combine' },
              inputs: {
                strings: [
                  {
                    sourceObject: 'newNode1',
                    sourcePath: '$.output',
                  },
                  {
                    sourceObject: 'newNode7',
                    sourcePath: '$.monthAbbreviation',
                  },
                  { sourceObject: 'newNode7', sourcePath: '$.day' },
                  { sourceObject: 'newNode9', sourcePath: '$.output' },
                ],
                delimiter: ' ',
              },
              name: 'Text Combine',
              position: {
                x: 1591.6176046728638,
                y: -36.74539301122354,
              },
            },
            newNode9: {
              type: 'STRING-CONCAT',
              config: {},
              inputs: {
                strings: [
                  { sourceObject: 'newNode7', sourcePath: '$.hour' },
                  ':',
                  {
                    sourceObject: 'newNode7',
                    sourcePath: '$.minute',
                  },
                  ' ',
                  'UTC',
                ],
              },
              position: {
                x: 1155.7585007564724,
                y: 100.49852089175835,
              },
            },
            newNode10: {
              type: 'GEOJSON-PARSER',
              config: { name: 'GeoJSON Parser' },
              inputs: {
                source: {
                  sourceObject: 'STATE',
                  sourcePath: '$.polyline',
                },
              },
              name: 'GeoJSON Parser',
              position: {
                x: 242.9071521542961,
                y: 1036.7821232071294,
              },
            },
            newNode11: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'newNode10',
                  sourcePath: '$.geojson',
                },
              },
              position: {
                x: 617.2337684419571,
                y: 1084.0196993962807,
              },
            },
            newNode13: {
              type: 'TO-JSON',
              config: {},
              inputs: {
                jsonObject: {
                  sourceObject: 'newNode10',
                  sourcePath: '$.geojson',
                },
              },
              position: {
                x: 643.6097665589148,
                y: 1236.464716250028,
              },
            },
            newNode14: {
              type: 'FROM-JSON',
              config: {
                type: 'Object',
                props: [['Object Array', 'coordinates']],
              },
              inputs: {
                from: {
                  sourceObject: 'newNode22',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 1695.533248535165,
                y: 921.2661313223425,
              },
            },
            newNode16: {
              type: 'ARRAY-CONCAT',
              config: { type: 'Number' },
              inputs: {
                array1: [
                  {
                    sourceObject: 'newNode24',
                    sourcePath: '$.value',
                  },
                  { sourceObject: 'newNode25', sourcePath: '$.value' },
                ],
              },
              position: {
                x: 892.1019812824491,
                y: 1575.6362463693963,
              },
            },
            newNode18: {
              type: 'TO-JSON',
              config: {},
              inputs: {
                jsonObject: {
                  sourceObject: 'newNode16',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 1386.8609756352732,
                y: 1592.7183127874057,
              },
            },
            newNode19: {
              type: 'STRING-CONCAT',
              config: {},
              inputs: {
                strings: [
                  '[',
                  {
                    sourceObject: 'newNode18',
                    sourcePath: '$.result',
                  },
                  ']',
                ],
              },
              position: {
                x: 1738.8247218588383,
                y: 1627.4615152043245,
              },
            },
            newNode20: {
              type: 'FROM-JSON',
              config: { type: 'Object Array' },
              inputs: {
                from: {
                  sourceObject: 'newNode19',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 2167.584717977991,
                y: 1623.5471538182485,
              },
            },
            newNode21: {
              type: 'OBJECT-GENERATOR',
              config: {
                props: [
                  ['String', 'type'],
                  ['Object Array', 'coordinates'],
                ],
              },
              inputs: {
                type: 'LineString',
                coordinates: {
                  sourceObject: 'newNode17',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 3133.964597355848,
                y: 591.6482005915947,
              },
            },
            newNode22: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'String' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode11',
                  sourcePath: '$.exists',
                },
                trueSource: {
                  sourceObject: 'newNode13',
                  sourcePath: '$.result',
                },
                falseSource: '{coordinates:[]}',
              },
              position: {
                x: 1164.4332488458522,
                y: 996.7315872399772,
              },
            },
            newNode23: {
              type: 'TO-JSON',
              config: {},
              inputs: {
                jsonObject: {
                  sourceObject: 'newNode21',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 3767.9786029938323,
                y: 710.1290378609436,
              },
            },
            newNode24: {
              type: 'TEXT-TO-NUMBER',
              config: {},
              inputs: {
                inText: {
                  sourceObject: 'newNode27',
                  sourcePath: '$.outText',
                },
              },
              position: {
                x: 227.6644006898198,
                y: 1255.2364580275223,
              },
            },
            newNode25: {
              type: 'TEXT-TO-NUMBER',
              config: {},
              inputs: {
                inText: {
                  sourceObject: 'newNode26',
                  sourcePath: '$.outText',
                },
              },
              position: {
                x: 209.255425522894,
                y: 1489.5629636533397,
              },
            },
            newNode26: {
              type: 'NUMBER-TO-TEXT',
              config: {},
              inputs: {
                input: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.lat',
                },
              },
              position: {
                x: -276.3998933322916,
                y: 1203.64289625013,
              },
            },
            newNode27: {
              type: 'NUMBER-TO-TEXT',
              config: {},
              inputs: {
                input: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.lon',
                },
              },
              position: {
                x: -273.80061999226245,
                y: 1025.592672458131,
              },
            },
            newNode12: {
              type: 'GEOJSON-PARSER',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'newNode23',
                  sourcePath: '$.result',
                },
              },
              position: {
                x: 4038.076872440326,
                y: 716.484473902287,
              },
            },
            newNode15: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'STATE',
                  sourcePath: '$.timestamps',
                },
              },
              position: {
                x: -326.8337062431145,
                y: 1683.3102557480192,
              },
            },
            newNode28: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'String' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode15',
                  sourcePath: '$.exists',
                },
                trueSource: {
                  sourceObject: 'STATE',
                  sourcePath: '$.timestamps',
                },
                falseSource: '[]',
              },
              position: {
                x: 66.1094333767216,
                y: 1732.4281482004988,
              },
            },
            newNode29: {
              type: 'FROM-JSON',
              config: { type: 'Number Array' },
              inputs: {
                from: {
                  sourceObject: 'newNode28',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 555.3287144494498,
                y: 1831.101525797311,
              },
            },
            newNode30: {
              type: 'DATE-STRING-PARSER',
              config: { name: 'Date String Parser' },
              inputs: {
                source: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
              },
              name: 'Date String Parser',
              position: {
                x: -1017.1844203631942,
                y: 1196.3180127859007,
              },
            },
            newNode31: {
              type: 'ADDITION',
              config: { name: 'Addition' },
              inputs: {
                number1: {
                  sourceObject: 'newNode30',
                  sourcePath: '$.dateNumber',
                },
                number2: {
                  sourceObject: 'newNode30',
                  sourcePath: '$.timeNumber',
                },
              },
              name: 'Addition',
              position: {
                x: -595.961330408616,
                y: 1878.274044942381,
              },
            },
            newNode32: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Number Array' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode15',
                  sourcePath: '$.exists',
                },
                falseSource: [
                  {
                    sourceObject: 'newNode31',
                    sourcePath: '$.result',
                  },
                ],
                trueSource: {
                  sourceObject: 'newNode35',
                  sourcePath: '$.reduced',
                },
              },
              position: {
                x: 2278.298694344961,
                y: 2044.7275225284025,
              },
            },
            newNode33: {
              type: 'TO-JSON',
              config: {},
              inputs: {
                jsonObject: {
                  sourceObject: 'newNode32',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 3176.628208611958,
                y: 2096.247856836685,
              },
            },
            newNode34: {
              type: 'ARRAY-REDUCE',
              config: {
                type: 'Number',
                functionId: 'findIndexGreaterThanValue::1',
                inputName: 'value',
                inputArrayName: 'fullArray',
                inputIndexName: 'index',
                outputName: 'result',
                inputData: true,
              },
              inputs: {
                data: {
                  sourceObject: 'newNode31',
                  sourcePath: '$.result',
                },
                input: {
                  sourceObject: 'newNode29',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 874.0757005397311,
                y: 1838.773121919409,
              },
            },
            newNode35: {
              type: 'ARRAY-REDUCE',
              config: {
                type: 'Number',
                functionId: 'insertItemAtIndex::1',
                inputName: 'in',
                inputArrayName: 'fullArray',
                inputIndexName: 'index',
                outputName: 'result',
                inputData: true,
              },
              inputs: {
                input: {
                  sourceObject: 'newNode29',
                  sourcePath: '$.output',
                },
                data: {
                  indexToInsert: {
                    sourceObject: 'newNode34',
                    sourcePath: '$.reduced',
                  },
                  valueToInsert: {
                    sourceObject: 'newNode31',
                    sourcePath: '$.result',
                  },
                },
              },
              position: {
                x: 1563.6676475352488,
                y: 1892.7000827470606,
              },
            },
            newNode36: {
              type: 'ARRAY-REDUCE',
              config: {
                type: 'Number',
                functionId: 'insertItemAtIndex::1',
                inputName: 'in',
                inputArrayName: 'fullArray',
                inputIndexName: 'index',
                outputName: 'result',
                inputData: true,
              },
              inputs: {
                data: {
                  indexToInsert: {
                    sourceObject: 'newNode34',
                    sourcePath: '$.reduced',
                  },
                  valueToInsert: {
                    sourceObject: 'newNode38',
                    sourcePath: '$.a',
                  },
                },
                input: {
                  sourceObject: 'newNode14',
                  sourcePath: '$.output.coordinates',
                },
              },
              position: {
                x: 2341.8143301267332,
                y: 930.5521594453344,
              },
            },
            newNode17: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Object Array' },
              inputs: {
                falseSource: {
                  sourceObject: 'newNode20',
                  sourcePath: '$.output',
                },
                trueSource: {
                  sourceObject: 'newNode36',
                  sourcePath: '$.reduced',
                },
                booleanSource: {
                  sourceObject: 'newNode15',
                  sourcePath: '$.exists',
                },
              },
              position: {
                x: 2851.13505452227,
                y: 655.6678869305395,
              },
            },
            newNode37: {
              type: 'OBJECT-GENERATOR',
              config: { props: [['Object Array', 'a']] },
              inputs: {
                a: {
                  sourceObject: 'newNode16',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 2489.7050048876717,
                y: 1596.0734579115028,
              },
            },
            newNode38: {
              type: 'OBJECT-ATTRIBUTE-LOOKUP',
              config: { props: [['Number', 'a']] },
              inputs: {
                input: {
                  sourceObject: 'newNode37',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 3084.6816476110384,
                y: 1617.6821046761613,
              },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
  pointsAdded: {
    versions: [
      {
        versionNumber: 1,
        eventSchema: {
          type: 'object',
          properties: {
            type: { type: 'string', enum: ['pointsAdded'] },
            version: { type: 'number' },
            timestamp: {
              description: 'Timestamp',
              type: 'string',
              format: 'date-time',
            },
            aggregateId: { type: 'string' },
            metadata: {
              type: 'object',
              properties: {
                timeFormOpened: {
                  type: 'string',
                  format: 'date-time',
                },
                latitude: { type: 'number' },
                longitude: { type: 'number' },
                selectedLatitude: { type: 'number' },
                selectedLongitude: { type: 'number' },
                locationAccuracy: { type: 'number' },
                aggregateType: { type: 'string' },
                userInfo: {
                  type: 'object',
                  properties: {
                    userName: { type: 'string' },
                    email: { type: 'string' },
                    authExpiration: {
                      type: 'string',
                      format: 'date-time',
                    },
                    familyName: { type: 'string' },
                    givenName: { type: 'string' },
                    authPayload: { type: 'object', properties: {} },
                    roles: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          id: { type: 'string' },
                          name: { type: 'string' },
                        },
                      },
                    },
                    roleString: { type: 'string' },
                    permissions: {
                      type: 'array',
                      items: { type: 'string' },
                    },
                    permissionString: { type: 'string' },
                  },
                },
                isOnline: { type: 'boolean' },
                selectedProjects: {
                  type: 'array',
                  items: { type: 'string' },
                },
              },
            },
            data: {
              type: 'object',
              properties: {
                project: { type: 'string' },
                locations: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      Lat: { type: 'string' },
                      Lon: { type: 'string' },
                      Timestamp: { type: 'string' },
                    },
                    required: ['Lat', 'Lon', 'Timestamp'],
                  },
                },
              },
              required: ['project', 'locations'],
            },
          },
        },
        aggregateMap: {
          nodes: {
            EVENT: {
              type: 'EVENT',
              config: {},
              inputs: {},
              position: {
                x: -1483.2035199787285,
                y: 264.8385867966779,
              },
            },
            STATE: {
              type: 'STATE',
              config: { name: 'Current Aggregate State' },
              inputs: {},
              name: 'Current Aggregate State',
              position: {
                x: -562.620626468373,
                y: 656.9829890518163,
              },
            },
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                id: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.aggregateId',
                },
                createdBy: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.output',
                },
                modifiedDateTime: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
                createdDateTime: {
                  sourceObject: 'newNode5',
                  sourcePath: '$.output',
                },
                polyline: {
                  sourceObject: 'newNode12',
                  sourcePath: '$.geojson',
                },
                symbolKey: 'line_solid_2_0000FF',
                title: {
                  sourceObject: 'newNode8',
                  sourcePath: '$.output',
                },
                timestamps: {
                  sourceObject: 'newNode33',
                  sourcePath: '$.result',
                },
                project: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.project',
                },
              },
              position: {
                x: 4268.678936821423,
                y: 503.1443173404304,
              },
            },
            newNode1: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'String' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode3',
                  sourcePath: '$.exists',
                },
                trueSource: {
                  sourceObject: 'newNode2',
                  sourcePath: '$.output',
                },
                falseSource: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.metadata.userInfo.userName',
                },
              },
              position: {
                x: 969.0593011767274,
                y: 467.6990821536299,
              },
            },
            newNode2: {
              type: 'STRING-CONCAT',
              config: { name: 'Text Combine' },
              inputs: {
                delimiter: ' ',
                strings: [
                  {
                    sourceObject: 'EVENT',
                    sourcePath: '$.metadata.userInfo.givenName',
                  },
                  {
                    sourceObject: 'EVENT',
                    sourcePath: '$.metadata.userInfo.familyName',
                  },
                ],
              },
              name: 'Text Combine',
              position: {
                x: 542.1270258542785,
                y: 606.7289009185772,
              },
            },
            newNode3: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.metadata.userInfo.givenName',
                },
              },
              position: {
                x: 551.9698448818854,
                y: 390.1868823112256,
              },
            },
            newNode4: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'STATE',
                  sourcePath: '$.createdDateTime',
                },
              },
              position: {
                x: 460.9928857054535,
                y: 948.235062665057,
              },
            },
            newNode5: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'String' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode4',
                  sourcePath: '$.exists',
                },
                trueSource: {
                  sourceObject: 'STATE',
                  sourcePath: '$.createdDateTime',
                },
                falseSource: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
              },
              position: {
                x: 956.4523326661688,
                y: 810.0435073988186,
              },
            },
            newNode6: {
              type: 'DATE-STRING-PARSER',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
              },
              position: {
                x: 474.4576450394811,
                y: -74.88631674320027,
              },
            },
            newNode7: {
              type: 'DATE-INFO',
              config: {},
              inputs: {
                date: {
                  sourceObject: 'newNode6',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 753.804549113468,
                y: -190.1790023674313,
              },
            },
            newNode8: {
              type: 'STRING-CONCAT',
              config: { name: 'Text Combine' },
              inputs: {
                strings: [
                  {
                    sourceObject: 'newNode1',
                    sourcePath: '$.output',
                  },
                  {
                    sourceObject: 'newNode7',
                    sourcePath: '$.monthAbbreviation',
                  },
                  { sourceObject: 'newNode7', sourcePath: '$.day' },
                  { sourceObject: 'newNode9', sourcePath: '$.output' },
                ],
                delimiter: ' ',
              },
              name: 'Text Combine',
              position: {
                x: 1591.6176046728638,
                y: -36.74539301122354,
              },
            },
            newNode9: {
              type: 'STRING-CONCAT',
              config: {},
              inputs: {
                strings: [
                  { sourceObject: 'newNode7', sourcePath: '$.hour' },
                  ':',
                  {
                    sourceObject: 'newNode7',
                    sourcePath: '$.minute',
                  },
                  ' ',
                  'UTC',
                ],
              },
              position: {
                x: 1155.7585007564724,
                y: 100.49852089175835,
              },
            },
            newNode10: {
              type: 'GEOJSON-PARSER',
              config: { name: 'GeoJSON Parser' },
              inputs: {
                source: {
                  sourceObject: 'STATE',
                  sourcePath: '$.polyline',
                },
              },
              name: 'GeoJSON Parser',
              position: {
                x: 242.9071521542961,
                y: 1036.7821232071294,
              },
            },
            newNode11: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'newNode10',
                  sourcePath: '$.geojson',
                },
              },
              position: {
                x: 617.2337684419571,
                y: 1084.0196993962807,
              },
            },
            newNode13: {
              type: 'TO-JSON',
              config: {},
              inputs: {
                jsonObject: {
                  sourceObject: 'newNode10',
                  sourcePath: '$.geojson',
                },
              },
              position: {
                x: 643.6097665589148,
                y: 1236.464716250028,
              },
            },
            newNode14: {
              type: 'FROM-JSON',
              config: {
                type: 'Object',
                props: [['Object Array', 'coordinates']],
              },
              inputs: {
                from: {
                  sourceObject: 'newNode22',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 1557.5511445578632,
                y: 743.2894440872785,
              },
            },
            newNode21: {
              type: 'OBJECT-GENERATOR',
              config: {
                props: [
                  ['String', 'type'],
                  ['Object Array', 'coordinates'],
                ],
              },
              inputs: {
                type: 'LineString',
                coordinates: {
                  sourceObject: 'newNode17',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 3133.964597355848,
                y: 591.6482005915947,
              },
            },
            newNode22: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'String' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode11',
                  sourcePath: '$.exists',
                },
                trueSource: {
                  sourceObject: 'newNode13',
                  sourcePath: '$.result',
                },
                falseSource: '{coordinates:[]}',
              },
              position: {
                x: 1097.6351237448341,
                y: 1011.7270847116343,
              },
            },
            newNode23: {
              type: 'TO-JSON',
              config: {},
              inputs: {
                jsonObject: {
                  sourceObject: 'newNode21',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 3744.8037432649076,
                y: 697.8599944750423,
              },
            },
            newNode12: {
              type: 'GEOJSON-PARSER',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'newNode23',
                  sourcePath: '$.result',
                },
              },
              position: {
                x: 4032.6239642688142,
                y: 702.8522034735079,
              },
            },
            newNode15: {
              type: 'TEXT-EXISTS',
              config: {},
              inputs: {
                source: {
                  sourceObject: 'STATE',
                  sourcePath: '$.timestamps',
                },
              },
              position: {
                x: -289.83676317008076,
                y: 1401.202407961478,
              },
            },
            newNode28: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'String' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode15',
                  sourcePath: '$.exists',
                },
                trueSource: {
                  sourceObject: 'STATE',
                  sourcePath: '$.timestamps',
                },
                falseSource: '[]',
              },
              position: {
                x: 66.1094333767216,
                y: 1732.4281482004988,
              },
            },
            newNode29: {
              type: 'FROM-JSON',
              config: { type: 'Number Array' },
              inputs: {
                from: {
                  sourceObject: 'newNode28',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 550.0717160131898,
                y: 1753.3923010949843,
              },
            },
            newNode32: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Number Array' },
              inputs: {
                booleanSource: {
                  sourceObject: 'newNode15',
                  sourcePath: '$.exists',
                },
                falseSource: {
                  sourceObject: 'newNode39',
                  sourcePath: '$.reduced.times',
                },
                trueSource: {
                  sourceObject: 'newNode35',
                  sourcePath: '$.reduced',
                },
              },
              position: {
                x: 2278.298694344961,
                y: 2044.7275225284025,
              },
            },
            newNode33: {
              type: 'TO-JSON',
              config: {},
              inputs: {
                jsonObject: {
                  sourceObject: 'newNode32',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 3176.628208611958,
                y: 2096.247856836685,
              },
            },
            newNode34: {
              type: 'ARRAY-REDUCE',
              config: {
                type: 'Number',
                functionId: 'findIndexGreaterThanValue::1',
                inputName: 'value',
                inputArrayName: 'fullArray',
                inputIndexName: 'index',
                outputName: 'result',
                inputData: true,
              },
              inputs: {
                data: {
                  sourceObject: 'newNode20',
                  sourcePath: '$.output',
                },
                input: {
                  sourceObject: 'newNode29',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 908.7812509179931,
                y: 1793.1523032655734,
              },
            },
            newNode35: {
              type: 'ARRAY-REDUCE',
              config: {
                type: 'Number',
                functionId: 'insertItemsAtIndex::1',
                inputName: 'in',
                inputArrayName: 'fullArray',
                inputIndexName: 'index',
                outputName: 'result',
                inputData: true,
              },
              inputs: {
                input: {
                  sourceObject: 'newNode29',
                  sourcePath: '$.output',
                },
                data: {
                  indexToInsert: {
                    sourceObject: 'newNode34',
                    sourcePath: '$.reduced',
                  },
                  valueToInsert: {
                    sourceObject: 'newNode39',
                    sourcePath: '$.reduced.times',
                  },
                },
              },
              position: {
                x: 1660.0836857401284,
                y: 1648.2805044378288,
              },
            },
            newNode36: {
              type: 'ARRAY-REDUCE',
              config: {
                type: 'Number',
                functionId: 'insertItemsAtIndex::1',
                inputName: 'in',
                inputArrayName: 'fullArray',
                inputIndexName: 'index',
                outputName: 'result',
                inputData: true,
              },
              inputs: {
                data: {
                  indexToInsert: {
                    sourceObject: 'newNode34',
                    sourcePath: '$.reduced',
                  },
                  valueToInsert: {
                    sourceObject: 'newNode39',
                    sourcePath: '$.reduced.coordinates',
                  },
                },
                input: {
                  sourceObject: 'newNode14',
                  sourcePath: '$.output.coordinates',
                },
              },
              position: {
                x: 2239.2306726901134,
                y: 894.6043459574861,
              },
            },
            newNode17: {
              type: 'CONDITIONAL-MAPPER',
              config: { type: 'Object Array' },
              inputs: {
                trueSource: {
                  sourceObject: 'newNode36',
                  sourcePath: '$.reduced',
                },
                booleanSource: {
                  sourceObject: 'newNode15',
                  sourcePath: '$.exists',
                },
                falseSource: {
                  sourceObject: 'newNode39',
                  sourcePath: '$.reduced.coordinates',
                },
              },
              position: {
                x: 2791.184854087848,
                y: 684.339721920915,
              },
            },
            newNode39: {
              type: 'ARRAY-REDUCE',
              config: {
                type: 'Object',
                functionId: 'transformBFTArray::1',
                inputName: 'in',
                outputName: 'result',
                inputData: false,
              },
              inputs: {
                input: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.locations',
                },
              },
              position: {
                x: -1039.3704634565338,
                y: 875.5533587555242,
              },
            },
            newNode20: {
              type: 'ARRAY-LOOKUP',
              config: { type: 'Number' },
              inputs: {
                input: {
                  sourceObject: 'newNode39',
                  sourcePath: '$.reduced.times',
                },
                index: 0,
              },
              position: {
                x: -54.961841827412904,
                y: 1956.7841290468225,
              },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
  generatedDefaultBlueForceTrackCleanup: {
    versions: [
      {
        eventSchema: {
          type: 'object',
          properties: {
            type: {
              type: 'string',
              enum: ['generatedDefaultBlueForceTrackCleanup'],
            },
            version: { type: 'number' },
            timestamp: {
              description: 'Timestamp',
              type: 'string',
              format: 'date-time',
            },
            aggregateId: { type: 'string' },
            metadata: {
              type: 'object',
              properties: {
                timeFormOpened: {
                  type: 'string',
                  format: 'date-time',
                },
                latitude: { type: 'number' },
                longitude: { type: 'number' },
                selectedLatitude: { type: 'number' },
                selectedLongitude: { type: 'number' },
                locationAccuracy: { type: 'number' },
                aggregateType: { type: 'string' },
                userInfo: {
                  type: 'object',
                  properties: {
                    userName: { type: 'string' },
                    email: { type: 'string' },
                    authExpiration: {
                      type: 'string',
                      format: 'date-time',
                    },
                    familyName: { type: 'string' },
                    givenName: { type: 'string' },
                    authPayload: { type: 'object', properties: {} },
                    roles: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          id: { type: 'string' },
                          name: { type: 'string' },
                        },
                      },
                    },
                    roleString: { type: 'string' },
                    permissions: {
                      type: 'array',
                      items: { type: 'string' },
                    },
                    permissionString: { type: 'string' },
                  },
                },
                isOnline: { type: 'boolean' },
                selectedProjects: {
                  type: 'array',
                  items: { type: 'string' },
                },
              },
            },
            data: {
              properties: {
                symbolKey: { type: 'string' },
                title: { type: 'string' },
                id: { type: 'string' },
                createdBy: { type: 'string' },
                createdDateTime: {
                  type: 'string',
                  format: 'date-time',
                },
                modifiedDateTime: {
                  type: 'string',
                  format: 'date-time',
                },
                polyline: {
                  anyOf: [{ $ref: '/Geography/GeoJSON' }, { type: 'string' }],
                },
                assetAttributes: { type: 'string' },
              },
              type: 'object',
            },
          },
        },
        versionNumber: 1,
        aggregateMap: {
          outputDefinition: { outputNode: 'OUTPUT' },
          nodes: {
            EVENT: { type: 'EVENT', name: 'Event' },
            ConditionalMapper: {
              type: 'CONDITIONAL-MAPPER',
              name: 'Conditional',
              inputs: {
                booleanSource: {
                  sourceObject: 'Exists',
                  sourcePath: '$.exists',
                },
                falseSource: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.aggregateId',
                },
                trueSource: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.id',
                },
              },
            },
            Exists: {
              type: 'TEXT-EXISTS',
              name: 'Exists',
              inputs: {
                source: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.id',
                },
              },
            },
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                symbolKey: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.symbolKey',
                },
                title: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.title',
                },
                id: {
                  sourceObject: 'ConditionalMapper',
                  sourcePath: '$.output',
                },
                createdBy: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.createdBy',
                },
                createdDateTime: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.createdDateTime',
                },
                modifiedDateTime: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.modifiedDateTime',
                },
                polyline: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.polyline',
                },
                assetAttributes: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.assetAttributes',
                },
              },
            },
          },
        },
      },
    ],
  },
};
BLUE_FORCE_TRACK_AGGREGATE_DEFAULT.commands = {
  addPoint: {
    defaultLabel: 'Add Point',
    versions: [
      {
        version: 1,
        isCreationCommand: true,
        cmdToEventsMapping: {
          mappingType: 'ONE-TO-MANY',
          mapping: {
            nodes: {
              COMMAND: {
                type: 'DATA',
                config: {},
                inputs: {},
                position: { x: 49.76667022705078, y: 116 },
              },
              OUTPUT: {
                type: 'OUTPUT-EVENT',
                config: {
                  objectSchema: 'BlueForceTrack::pointAdded::1',
                  aggregateType: 'BlueForceTrack',
                  name: 'Output Event',
                },
                inputs: {
                  data: {
                    lat: {
                      sourceObject: 'COMMAND',
                      sourcePath: '$.Lat',
                    },
                    lon: {
                      sourceObject: 'COMMAND',
                      sourcePath: '$.Lon',
                    },
                    project: {
                      sourceObject: 'COMMAND',
                      sourcePath: '$.Project',
                    },
                  },
                  aggregateId: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.Id',
                  },
                },
                name: 'Output Event',
                position: { x: 1047, y: 27.800003051757812 },
              },
            },
            outputDefinition: {
              outputQuery: { types: ['OUTPUT-EVENT'] },
            },
          },
        },
        commandType: 'FORM',
        formDefinition: {
          order: ['Lat', 'Lon', 'Id', 'Project'],
          initialDataMap: {
            nodes: {
              STATE: {
                type: 'STATE',
                name: 'Current State',
                inputs: {},
              },
              OUTPUT: {
                type: 'OBJECT-BUILDER',
                name: 'Form State',
                inputs: {},
                config: { objectSchema: 'FORM' },
              },
            },
            outputDefinition: { outputNode: 'OUTPUT' },
          },
          components: {
            Lat: {
              type: 'numberInput',
              label: 'Lat',
              placeholder: 'Enter number',
              required: false,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              droppableId: 'form',
            },
            Lon: {
              type: 'numberInput',
              label: 'Lon',
              placeholder: 'Enter number',
              required: false,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              droppableId: 'form',
            },
            Id: {
              type: 'textInput',
              label: 'Id',
              placeholder: 'Enter text',
              required: false,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              droppableId: 'form',
            },
            Project: {
              type: 'textInput',
              label: 'Project',
              placeholder: 'Enter text',
              required: false,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              droppableId: 'form',
            },
          },
        },
        isImportCommand: false,
        isRedirectToDetails: false,
        additionalActions: {
          workflowConfirmationText: '',
          actions: [
            {
              label: '',
              type: 'COMMAND',
              aggregateName: '',
              action: { commandName: '', commandVersion: 0 },
            },
          ],
        },
        isPublic: false,
        hasNoAggregateIdCommand: false,
        fabOptionEnabled: true,
      },
    ],
  },
  addMultiplePoints: {
    defaultLabel: 'Add Multiple Points',
    versions: [
      {
        version: 1,
        isCreationCommand: true,
        cmdToEventsMapping: {
          mappingType: 'ONE-TO-MANY',
          mapping: {
            nodes: {
              COMMAND: {
                type: 'DATA',
                config: {},
                position: { x: 67.76667022705078, y: 50 },
              },
              OUTPUT: {
                type: 'OUTPUT-EVENT',
                config: {
                  objectSchema: 'BlueForceTrack::pointsAdded::1',
                  aggregateType: 'BlueForceTrack',
                },
                inputs: {
                  aggregateId: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.Id',
                  },
                  data: {
                    project: {
                      sourceObject: 'COMMAND',
                      sourcePath: '$.Project',
                    },
                    locations: {
                      sourceObject: 'COMMAND',
                      sourcePath: '$.Locations',
                    },
                  },
                },
                position: { x: 1191, y: 66.80000305175781 },
              },
            },
            outputDefinition: {
              outputQuery: { types: ['OUTPUT-EVENT'] },
            },
          },
        },
        commandType: 'FORM',
        formDefinition: {
          order: ['Locations', 'Project', 'Id'],
          initialDataMap: {
            nodes: {
              STATE: {
                type: 'STATE',
                name: 'Current State',
                inputs: {},
              },
              OUTPUT: {
                type: 'OBJECT-BUILDER',
                name: 'Form State',
                inputs: {},
                config: { objectSchema: 'FORM' },
              },
            },
            outputDefinition: { outputNode: 'OUTPUT' },
          },
          components: {
            Locations: {
              template: {
                order: ['Lat', 'Lon', 'Timestamp'],
                components: {
                  Lat: {
                    type: 'numberInput',
                    label: 'Lat',
                    placeholder: 'Enter number',
                    required: false,
                    repeats: {
                      enabled: false,
                      min: 1,
                      max: 1,
                      addButtonText: 'Add Field',
                      deleteConfirmationMessage: 'Are you sure you want to delete this?',
                      allowReordering: true,
                      allowInitialDataDelete: true,
                      allowAdding: true,
                    },
                    droppableId: 'Locations',
                  },
                  Lon: {
                    type: 'numberInput',
                    label: 'Lon',
                    placeholder: 'Enter number',
                    required: false,
                    repeats: {
                      enabled: false,
                      min: 1,
                      max: 1,
                      addButtonText: 'Add Field',
                      deleteConfirmationMessage: 'Are you sure you want to delete this?',
                      allowReordering: true,
                      allowInitialDataDelete: true,
                      allowAdding: true,
                    },
                    droppableId: 'Locations',
                  },
                  Timestamp: {
                    type: 'numberInput',
                    label: 'Timestamp',
                    placeholder: 'Enter number',
                    required: false,
                    repeats: {
                      enabled: false,
                      min: 1,
                      max: 1,
                      addButtonText: 'Add Field',
                      deleteConfirmationMessage: 'Are you sure you want to delete this?',
                      allowReordering: true,
                      allowInitialDataDelete: true,
                      allowAdding: true,
                    },
                    droppableId: 'Locations',
                  },
                },
              },
              type: 'group',
              label: 'Locations',
              repeats: {
                enabled: true,
                min: 1,
                max: -1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
            },
            Project: {
              type: 'textInput',
              label: 'Project',
              placeholder: 'Enter text',
              required: false,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              droppableId: 'form',
            },
            Id: {
              type: 'textInput',
              label: 'Id',
              placeholder: 'Enter text',
              required: false,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              droppableId: 'form',
            },
          },
        },
        isImportCommand: false,
        isRedirectToDetails: false,
        additionalActions: {
          workflowConfirmationText: '',
          actions: [
            {
              label: '',
              type: 'COMMAND',
              aggregateName: '',
              action: { commandName: '', commandVersion: 0 },
            },
          ],
        },
        isPublic: false,
        hasNoAggregateIdCommand: false,
        fabOptionEnabled: true,
      },
    ],
  },
  cleanup: {
    defaultLabel: 'Cleanup',
    versions: [
      {
        version: 1,
        isCreationCommand: false,
        cmdToEventsMapping: {
          mappingType: 'ONE-TO-MANY',
          mapping: {
            nodes: {
              COMMAND: {
                type: 'DATA',
                config: {},
                position: {
                  x: -529.2681184851596,
                  y: -27.913043478260747,
                },
              },
              OUTPUT: {
                type: 'OUTPUT-EVENT',
                config: {
                  objectSchema: 'BlueForceTrack::generatedDefaultBlueForceTrackCleanup::1',
                  aggregateType: 'BlueForceTrack',
                },
                inputs: {
                  aggregateId: {
                    sourceObject: 'newNode5',
                    sourcePath: '$.id',
                  },
                  outputEvent: {
                    sourceObject: 'newNode12',
                    sourcePath: '$.output',
                  },
                  data: {
                    symbolKey: {
                      sourceObject: 'newNode13',
                      sourcePath: '$.output',
                    },
                  },
                },
                position: {
                  x: 4071.2008123800165,
                  y: -49.94996148229728,
                },
              },
              newNode1: {
                type: 'ARRAY-ITERATOR',
                config: {},
                inputs: {
                  input: {
                    sourceObject: 'newNode2',
                    sourcePath: '$.itemsArray',
                  },
                },
                position: {
                  x: -1024.213645663475,
                  y: 544.4531351375136,
                },
              },
              newNode2: {
                type: 'ADVANCED-SEARCH',
                config: {
                  recordType: 'BlueForceTrack',
                  selectValues: ['"record"."id"', '"record"."modifiedDateTime"'],
                },
                position: {
                  x: -1465.5123904109876,
                  y: 502.86928994763423,
                },
              },
              newNode3: {
                type: 'OUTPUT-EVENT',
                config: {
                  aggregateType: 'BlueForceTrack',
                  objectSchema: 'BlueForceTrack::DeleteEvent::1',
                  name: 'Output Event',
                },
                inputs: {
                  aggregateId: {
                    sourceObject: 'newNode5',
                    sourcePath: '$.id',
                  },
                  outputEvent: {
                    sourceObject: 'newNode11',
                    sourcePath: '$.isGreater',
                  },
                },
                name: 'Output Event',
                position: {
                  x: 4179.666823891814,
                  y: 345.3128843579596,
                },
              },
              newNode4: {
                type: 'METADATA',
                config: {},
                position: {
                  x: -362.6662689795805,
                  y: -237.74583467646605,
                },
              },
              newNode5: {
                type: 'OBJECT-ATTRIBUTE-LOOKUP',
                config: {
                  props: [
                    ['String', 'id'],
                    ['Date', 'modifiedDateTime'],
                  ],
                },
                inputs: {
                  input: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.output',
                  },
                },
                position: {
                  x: -612.9463004288831,
                  y: 515.0031202087634,
                },
              },
              newNode6: {
                type: 'DATE-STRING-PARSER',
                config: {},
                inputs: {
                  source: {
                    sourceObject: 'newNode4',
                    sourcePath: '$.timeFormOpened',
                  },
                },
                position: {
                  x: -113.48525605216588,
                  y: -201.23014733751182,
                },
              },
              newNode7: {
                type: 'DATE-STRING-PARSER',
                config: { name: 'Date String Parser' },
                inputs: {
                  source: {
                    sourceObject: 'newNode5',
                    sourcePath: '$.modifiedDateTime',
                  },
                },
                name: 'Date String Parser',
                position: {
                  x: -123.71985627140124,
                  y: 145.5714951206624,
                },
              },
              newNode8: {
                type: 'SUBTRACTION',
                config: {},
                inputs: {
                  number1: {
                    sourceObject: 'newNode39',
                    sourcePath: '$.result',
                  },
                  number2: {
                    sourceObject: 'newNode40',
                    sourcePath: '$.result',
                  },
                },
                position: {
                  x: 688.4943687702297,
                  y: 51.22110320870691,
                },
              },
              newNode9: {
                type: 'DIVISION',
                config: {},
                inputs: {
                  number2: 86400000,
                  number1: {
                    sourceObject: 'newNode8',
                    sourcePath: '$.result',
                  },
                },
                position: {
                  x: 963.4689606029274,
                  y: -102.99369380214834,
                },
              },
              newNode11: {
                type: 'NUMBER-COMPARE',
                config: { name: 'Number Compare' },
                inputs: {
                  baseNumber: {
                    sourceObject: 'newNode9',
                    sourcePath: '$.result',
                  },
                  compareNumber: {
                    sourceObject: 'newNode38',
                    sourcePath: '$.result',
                  },
                },
                name: 'Number Compare',
                position: {
                  x: 1863.8933364003146,
                  y: -51.115530462971876,
                },
              },
              newNode12: {
                type: 'NOT',
                config: {},
                inputs: {
                  input: {
                    sourceObject: 'newNode11',
                    sourcePath: '$.isGreater',
                  },
                },
                position: {
                  x: 2896.2267457698595,
                  y: 45.69026585822613,
                },
              },
              newNode13: {
                type: 'STRING-CONCAT',
                config: {},
                inputs: {
                  strings: [
                    'line_solid_2_',
                    {
                      sourceObject: 'newNode24',
                      sourcePath: '$.output',
                    },
                    {
                      sourceObject: 'newNode19',
                      sourcePath: '$.output',
                    },
                    '00',
                    {
                      sourceObject: 'newNode30',
                      sourcePath: '$.output',
                    },
                    {
                      sourceObject: 'newNode31',
                      sourcePath: '$.output',
                    },
                  ],
                },
                position: {
                  x: 3025.5937206506164,
                  y: 365.5234570357474,
                },
              },
              newNode15: {
                type: 'MULTIPLICATION',
                config: {},
                inputs: {
                  number1: {
                    sourceObject: 'newNode17',
                    sourcePath: '$.result',
                  },
                  number2: 255,
                },
                position: {
                  x: 488.3651384272999,
                  y: 495.85197675662545,
                },
              },
              newNode17: {
                type: 'DIVISION',
                config: {},
                inputs: {
                  number1: {
                    sourceObject: 'newNode9',
                    sourcePath: '$.result',
                  },
                  number2: {
                    sourceObject: 'newNode38',
                    sourcePath: '$.result',
                  },
                },
                position: {
                  x: 879.4742867091601,
                  y: 235.1121020177091,
                },
              },
              newNode14: {
                type: 'ROUND',
                config: {},
                inputs: {
                  inputNum: {
                    sourceObject: 'newNode15',
                    sourcePath: '$.result',
                  },
                  roundDigit: 0,
                },
                position: {
                  x: 431.6628859041062,
                  y: 731.3086797692494,
                },
              },
              newNode16: {
                type: 'SUBTRACTION',
                config: {},
                inputs: {
                  number2: {
                    sourceObject: 'newNode14',
                    sourcePath: '$.result',
                  },
                  number1: 255,
                },
                position: {
                  x: 439.98691856934624,
                  y: 1157.706097560175,
                },
              },
              newNode18: {
                type: 'MODULO',
                config: {},
                inputs: {
                  value: {
                    sourceObject: 'newNode14',
                    sourcePath: '$.result',
                  },
                  modulus: 16,
                },
                position: {
                  x: 1257.4209046754513,
                  y: 272.76701099495085,
                },
              },
              newNode19: {
                type: 'VALUE-MAPPER',
                config: {
                  mappings: [
                    ['10', 'a'],
                    ['11', 'b'],
                    ['12', 'c'],
                    ['13', 'd'],
                    ['14', 'e'],
                    ['15', 'f'],
                  ],
                },
                inputs: {
                  source: {
                    sourceObject: 'newNode20',
                    sourcePath: '$.outText',
                  },
                  fallback: {
                    sourceObject: 'newNode20',
                    sourcePath: '$.outText',
                  },
                },
                position: {
                  x: 2162.1005417283927,
                  y: 204.4863577350991,
                },
              },
              newNode20: {
                type: 'NUMBER-TO-TEXT',
                config: {},
                inputs: {
                  input: {
                    sourceObject: 'newNode18',
                    sourcePath: '$.result',
                  },
                },
                position: {
                  x: 1788.580788145634,
                  y: 287.6776046916924,
                },
              },
              newNode21: {
                type: 'DIVISION',
                config: {},
                inputs: {
                  number1: {
                    sourceObject: 'newNode14',
                    sourcePath: '$.result',
                  },
                  number2: 16,
                },
                position: {
                  x: 941.8359600620049,
                  y: 542.0265702477839,
                },
              },
              newNode22: {
                type: 'ROUND',
                config: {},
                inputs: {
                  inputNum: {
                    sourceObject: 'newNode21',
                    sourcePath: '$.result',
                  },
                  roundDigit: 0,
                },
                position: {
                  x: 1370.9298851524027,
                  y: 539.0979469799009,
                },
              },
              newNode23: {
                type: 'NUMBER-TO-TEXT',
                config: {},
                inputs: {
                  input: {
                    sourceObject: 'newNode37',
                    sourcePath: '$.output',
                  },
                },
                position: {
                  x: 1873.7111701558445,
                  y: 504.3490564847627,
                },
              },
              newNode24: {
                type: 'VALUE-MAPPER',
                config: {
                  mappings: [
                    ['10', 'a'],
                    ['11', 'b'],
                    ['12', 'c'],
                    ['13', 'd'],
                    ['14', 'e'],
                    ['15', 'f'],
                  ],
                },
                inputs: {
                  source: {
                    sourceObject: 'newNode23',
                    sourcePath: '$.outText',
                  },
                  fallback: {
                    sourceObject: 'newNode23',
                    sourcePath: '$.outText',
                  },
                },
                position: {
                  x: 2157.2029675507765,
                  y: 567.2109672527208,
                },
              },
              newNode25: {
                type: 'DIVISION',
                config: {},
                inputs: {
                  number1: {
                    sourceObject: 'newNode16',
                    sourcePath: '$.result',
                  },
                  number2: 16,
                },
                position: {
                  x: 955.1511874358047,
                  y: 1147.8557935557671,
                },
              },
              newNode26: {
                type: 'ROUND',
                config: {},
                inputs: {
                  roundDigit: 0,
                  inputNum: {
                    sourceObject: 'newNode25',
                    sourcePath: '$.result',
                  },
                },
                position: {
                  x: 1383.9638330145742,
                  y: 1123.9078313396412,
                },
              },
              newNode27: {
                type: 'NUMBER-TO-TEXT',
                config: {},
                inputs: {
                  input: {
                    sourceObject: 'newNode32',
                    sourcePath: '$.output',
                  },
                },
                position: {
                  x: 1912.544438292941,
                  y: 1109.4919812842872,
                },
              },
              newNode28: {
                type: 'MODULO',
                config: {},
                inputs: {
                  value: {
                    sourceObject: 'newNode16',
                    sourcePath: '$.result',
                  },
                  modulus: 16,
                },
                position: {
                  x: 1132.9022865226696,
                  y: 1321.1441420154536,
                },
              },
              newNode29: {
                type: 'NUMBER-TO-TEXT',
                config: {},
                inputs: {
                  input: {
                    sourceObject: 'newNode28',
                    sourcePath: '$.result',
                  },
                },
                position: {
                  x: 1716.1262611867583,
                  y: 1370.4306750856583,
                },
              },
              newNode30: {
                type: 'VALUE-MAPPER',
                config: {
                  mappings: [
                    ['10', 'a'],
                    ['11', 'b'],
                    ['12', 'c'],
                    ['13', 'd'],
                    ['14', 'e'],
                    ['15', 'f'],
                  ],
                },
                inputs: {
                  source: {
                    sourceObject: 'newNode27',
                    sourcePath: '$.outText',
                  },
                  fallback: {
                    sourceObject: 'newNode27',
                    sourcePath: '$.outText',
                  },
                },
                position: {
                  x: 2245.381229240277,
                  y: 929.9878383166041,
                },
              },
              newNode31: {
                type: 'VALUE-MAPPER',
                config: {
                  mappings: [
                    ['10', 'a'],
                    ['11', 'b'],
                    ['12', 'c'],
                    ['13', 'd'],
                    ['14', 'e'],
                    ['15', 'f'],
                  ],
                },
                inputs: {
                  source: {
                    sourceObject: 'newNode29',
                    sourcePath: '$.outText',
                  },
                  fallback: {
                    sourceObject: 'newNode29',
                    sourcePath: '$.outText',
                  },
                },
                position: {
                  x: 2181.844517222801,
                  y: 1328.6495607791996,
                },
              },
              newNode32: {
                type: 'CONDITIONAL-MAPPER',
                config: { type: 'Number' },
                inputs: {
                  booleanSource: {
                    sourceObject: 'newNode34',
                    sourcePath: '$.isLess',
                  },
                  trueSource: {
                    sourceObject: 'newNode33',
                    sourcePath: '$.result',
                  },
                  falseSource: {
                    sourceObject: 'newNode26',
                    sourcePath: '$.result',
                  },
                },
                position: {
                  x: 1727.4491941577876,
                  y: 900.3816111886356,
                },
              },
              newNode33: {
                type: 'SUBTRACTION',
                config: {},
                inputs: {
                  number1: {
                    sourceObject: 'newNode26',
                    sourcePath: '$.result',
                  },
                  number2: 1,
                },
                position: {
                  x: 1230.5065491157188,
                  y: 920.2112804183511,
                },
              },
              newNode34: {
                type: 'NUMBER-COMPARE',
                config: {},
                inputs: {
                  baseNumber: {
                    sourceObject: 'newNode25',
                    sourcePath: '$.result',
                  },
                  compareNumber: {
                    sourceObject: 'newNode26',
                    sourcePath: '$.result',
                  },
                },
                position: {
                  x: 916.402738489668,
                  y: 948.2464589360104,
                },
              },
              newNode35: {
                type: 'NUMBER-COMPARE',
                config: {},
                inputs: {
                  baseNumber: {
                    sourceObject: 'newNode21',
                    sourcePath: '$.result',
                  },
                  compareNumber: {
                    sourceObject: 'newNode22',
                    sourcePath: '$.result',
                  },
                },
                position: {
                  x: 923.7975083617782,
                  y: 729.923099717108,
                },
              },
              newNode36: {
                type: 'SUBTRACTION',
                config: {},
                inputs: {
                  number2: 1,
                  number1: {
                    sourceObject: 'newNode22',
                    sourcePath: '$.result',
                  },
                },
                position: {
                  x: 1259.351502896145,
                  y: 762.6813304753185,
                },
              },
              newNode37: {
                type: 'CONDITIONAL-MAPPER',
                config: { type: 'Number' },
                inputs: {
                  booleanSource: {
                    sourceObject: 'newNode35',
                    sourcePath: '$.isLess',
                  },
                  trueSource: {
                    sourceObject: 'newNode36',
                    sourcePath: '$.result',
                  },
                  falseSource: {
                    sourceObject: 'newNode22',
                    sourcePath: '$.result',
                  },
                },
                position: {
                  x: 1765.697430077785,
                  y: 690.4154424220101,
                },
              },
              newNode38: {
                type: 'ADDITION',
                config: { name: 'Max days to keep paths' },
                inputs: { number1: 2, number2: 0 },
                name: 'Max days to keep paths',
                position: {
                  x: -748.6414924667284,
                  y: 285.1761005600101,
                },
              },
              newNode39: {
                type: 'ADDITION',
                config: {},
                inputs: {
                  number1: {
                    sourceObject: 'newNode6',
                    sourcePath: '$.dateNumber',
                  },
                  number2: {
                    sourceObject: 'newNode6',
                    sourcePath: '$.timeNumber',
                  },
                },
                position: {
                  x: 250.00929144362178,
                  y: 8.03453097277442,
                },
              },
              newNode40: {
                type: 'ADDITION',
                config: {},
                inputs: {
                  number1: {
                    sourceObject: 'newNode7',
                    sourcePath: '$.dateNumber',
                  },
                  number2: {
                    sourceObject: 'newNode7',
                    sourcePath: '$.timeNumber',
                  },
                },
                position: {
                  x: 263.4685326873641,
                  y: 245.8144596122213,
                },
              },
            },
            outputDefinition: {
              outputQuery: { types: ['OUTPUT-EVENT'] },
            },
          },
        },
        commandType: 'TIMER',
        isImportCommand: false,
        isRedirectToDetails: false,
        additionalActions: {
          workflowConfirmationText: '',
          actions: [
            {
              label: '',
              type: 'COMMAND',
              aggregateName: '',
              action: { commandName: '', commandVersion: 0 },
            },
          ],
        },
        isPublic: false,
      },
    ],
  },
};
const BLUE_FORCE_TRACK_SCHEDULE: TimerAction[] = [
  {
    timer: 'cleanup ver:1',
    startTime: '00:00',
    repeat: '01H 00M',
    days: 'M, Tu, W, Th, F, Sa, Su',
    endTime: '23:59',
  },
];

export const generateBlueForceTrackAggregateData = (config: Gen5Config) => {
  addBlueForceTrackFunctions(config);
  generateAggregateData(
    config,
    BLUE_FORCE_TRACK_AGGREGATE_DEFAULT,
    undefined,
    BLUE_FORCE_TRACK_SCHEDULE,
    true
  );
};
export const removeBlueForceTrackAggregateData = (config: Gen5Config) => {
  removeAggregateData(config, BLUE_FORCE_TRACK_DEFAULT_KEYS.typeName);
};
//#endregion

const generateAggregateData = (
  config: Gen5Config,
  aggregateDefault: AggregateDefault,
  webUIDefault?: WebUIDefault,
  defaultSchedules?: TimerAction[],
  linkProject?: boolean
) => {
  const aggregateIndex = getAggregateIndex(config, aggregateDefault.typeName);

  const aggregate =
    (aggregateIndex !== -1 && config.aggregates[aggregateIndex]) ||
    generateAggregateByName(aggregateDefault.typeName);
  aggregate.singularName = aggregateDefault.singularName ?? aggregateDefault.typeName;
  aggregate.pluralName = `${aggregateDefault.singularName ?? aggregateDefault.typeName}s`;
  aggregate.isReferenceType = aggregateDefault.isReferenceType;
  if (aggregateDefault.primaryLocationProperty) {
    aggregate.primaryLocationProperty = aggregateDefault.primaryLocationProperty as
      | string
      | LocationProperty;
  }

  if (aggregateDefault.properties) {
    // Assign default values to standard aggregate properties (this preserves property order and values not specified in the default).
    const standardKeys = Object.keys(aggregate.properties);
    standardKeys.forEach((propertyName) => {
      const standardDefault = aggregateDefault.properties![propertyName];
      if (standardDefault) _.assign(aggregate.properties[propertyName], standardDefault);
    });
    // Push non-standard aggregate properties.
    const nonStandardProps = standardKeys.reduce((acc, cur, i, a) => {
      const { [cur]: _, ...rest } = acc;
      return rest;
    }, aggregateDefault.properties);

    const filteredProps: PropertyCollection = {
      id: { type: 'ID', label: 'ID' },
      ...Object.fromEntries(
        Object.entries(aggregate.properties).filter(([key]) => key !== 'lat' && key !== 'lon')
      ),
    };

    aggregate.properties = {
      ...(aggregateDefault.typeName === FLOOR_PLAN_DEFAULT_KEYS.typeName
        ? filteredProps
        : aggregate.properties),
      ...(nonStandardProps as PropertyCollection),
    };
  }

  generateAggregateEvents(aggregate, aggregateDefault.events);
  generateAggregateCommands(aggregate, aggregateDefault.commands);

  if (aggregateIndex !== -1) config.aggregates[aggregateIndex] = aggregate;
  else config.aggregates.push(aggregate);

  if (webUIDefault) {
    generateWebUI(config.webUIConfig, webUIDefault, aggregate);
  }

  if (defaultSchedules) {
    generateSchedules(config, defaultSchedules, aggregateDefault.typeName);
  }

  if (linkProject && aggregate.objectStore?.table) {
    const projIndex = getAggregateIndex(config, 'Project');
    config.aggregates[projIndex].properties[aggregate.objectStore.table] = {
      type: aggregate.typeName,
      relation: 'ONE-TO-MANY',
      label: aggregate.pluralName,
      foreignColumn: 'project',
      nullable: true,
    };
  }

  return config;
};

const generateAggregateEvents = (
  aggregate: AggregateConfig,
  eventDefaults: AggregateDefault['events']
) => {
  aggregate.events = aggregate.events ?? {};

  Object.entries(eventDefaults ?? {}).forEach(([eventName, eventDefinition]) => {
    const versionDefaults = eventDefinition?.versions as EventVersionDefinition[];
    const newEvent = generateNewEventAndVersions(
      eventName,
      versionDefaults.map<EventVersionDefault>((v) => ({
        versionNumber: v.versionNumber,
        dataSchema: v.eventSchema?.properties?.data as JSONSchema6,
        aggregateMap: v.aggregateMap,
      }))
    );

    aggregate.events = { ...aggregate.events, [eventName]: newEvent };
  });

  return aggregate;
};

const generateAggregateCommands = (
  aggregate: AggregateConfig,
  commandDefaults: AggregateDefault['commands']
) => {
  aggregate.commands = aggregate.commands ?? {};

  Object.entries(commandDefaults ?? {}).forEach(([commandName, commandDefinition]) => {
    const versionDefaults = commandDefinition?.versions as CommandVersionDefinition[];
    const newCommand = generateNewCommandAndVersions(
      commandName,
      versionDefaults.map<CommandVersionDefault>((v) => ({
        commandType: v.commandType,
        version: v.version,
        eventName:
          v.cmdToEventsMapping?.mappingType === 'ONE-TO-MANY'
            ? undefined
            : v.cmdToEventsMapping?.eventName,
        isCreationCommand: v.isCreationCommand,
        cmdToEventsMapping: v.cmdToEventsMapping,
        formDefinition: v.commandType === 'FORM' ? v.formDefinition : undefined,
        hasNoAggregateIdCommand: v.hasNoAggregateIdCommand,
        fabOptionEnabled: v.fabOptionEnabled,
      })),
      commandDefinition?.defaultLabel
    );

    aggregate.commands = { ...aggregate.commands, [commandName]: newCommand };
  });

  return aggregate;
};

const generateWebUI = (
  webUI: WebUIConfig,
  webUIDefault: WebUIDefault,
  aggregate?: AggregateConfig | AggregateDefault
) => {
  // FAB Actions
  if (webUIDefault.fabActions) {
    const faDefaults = webUIDefault.fabActions as FabAction[];
    faDefaults
      .filter((fa) => !aggregate?.typeName || fa.aggregateName === aggregate.typeName)
      .forEach((fa) => webUI.fabActions.push(fa));
  }

  // Aggregate Customizations
  if (webUIDefault.aggregateUICustomizations) {
    webUI.aggregateUICustomizations = webUI.aggregateUICustomizations ?? {};

    const acDefaults =
      webUIDefault.aggregateUICustomizations as WebUIConfig['aggregateUICustomizations'];
    Object.entries(acDefaults ?? {})
      .filter(([acName]) => !aggregate?.typeName || acName === aggregate.typeName)
      .forEach(([acName, acDefinition]) => {
        webUI.aggregateUICustomizations![acName] = {
          ...(webUI.aggregateUICustomizations![acName] ??
            defaultAggregateUICustomization(
              'webUIConfig',
              aggregate?.labelProperty ?? AGGREGATE_DEFAULT_KEYS.labelProperty
            )),
          ...acDefinition,
        };
      });
  }

  return webUI;
};

const generateSchedules = (config: Gen5Config, timers: TimerAction[], aggregateName: string) => {
  config.schedules = {
    ...config.schedules,
    [aggregateName]: [...(config.schedules?.[aggregateName] || []), ...timers],
  };
};

const removeAggregateData = (config: Gen5Config, aggregateTypeName: string) => {
  // Aggregate, Events, Commands
  const aggregateIndex = getAggregateIndex(config, aggregateTypeName);
  if (aggregateIndex !== -1) {
    config.aggregates.splice(aggregateIndex, 1);
  }

  //Remove any other aggregates referencing the removed type
  config.aggregates = config.aggregates.map((a) => {
    const newProps = Object.keys(a.properties).reduce<PropertyCollection>(
      (prev, curr) => {
        const prop = a.properties[curr];
        if (prop.type !== aggregateTypeName) {
          prev[curr] = prop;
        }
        return prev;
      },
      { id: a.properties.id }
    );
    return { ...a, properties: newProps };
  });

  // WebUI
  if (
    config.webUIConfig.fabActions &&
    config.webUIConfig.fabActions.findIndex((fa) => fa.aggregateName === aggregateTypeName) !== -1
  ) {
    config.webUIConfig.fabActions = [
      ...config.webUIConfig.fabActions.filter((fa) => fa.aggregateName !== aggregateTypeName),
    ];
  }
  if (
    config.webUIConfig.importActions &&
    config.webUIConfig.importActions.findIndex((fa) => fa.aggregateName === aggregateTypeName) !==
      -1
  ) {
    config.webUIConfig.importActions = [
      ...config.webUIConfig.importActions.filter((fa) => fa.aggregateName !== aggregateTypeName),
    ];
  }
  if (
    config.webUIConfig.aggregateUICustomizations &&
    config.webUIConfig.aggregateUICustomizations[aggregateTypeName]
  ) {
    delete config.webUIConfig.aggregateUICustomizations[aggregateTypeName];
  }

  // MobileUI
  if (
    config.mobileUIConfig.fabActions &&
    config.mobileUIConfig.fabActions.findIndex((fa) => fa.aggregateName === aggregateTypeName) !==
      -1
  ) {
    config.mobileUIConfig.fabActions = [
      ...config.mobileUIConfig.fabActions.filter((fa) => fa.aggregateName !== aggregateTypeName),
    ];
  }
  if (
    config.mobileUIConfig.aggregateUICustomizations &&
    config.mobileUIConfig.aggregateUICustomizations[aggregateTypeName]
  ) {
    delete config.mobileUIConfig.aggregateUICustomizations[aggregateTypeName];
  }

  // Schedules and Custom Pages
  if (config.schedules && config.schedules[aggregateTypeName]) {
    delete config.schedules[aggregateTypeName];
  }
  if (config.pagesConfig) {
    Object.entries(config.pagesConfig).forEach(([pageName, pageConfig]) => {
      if (pageConfig.aggregateType === aggregateTypeName) {
        delete config.pagesConfig![pageName];
      }
    });
  }

  return config;
};

const removePropertiesFromAggregatesByType = (config: Gen5Config, aggregateTypeName: string) => {
  Object.entries(config.aggregates).forEach(([recordType, aggregate]) => {
    const filteredProps: PropertyCollection = {
      id: { type: 'ID', label: 'ID' },
      ...Object.fromEntries(
        Object.entries(aggregate.properties).filter(([, prop]) => prop.type !== aggregateTypeName)
      ),
    };

    aggregate.properties = filteredProps;
  });
};
